
import { isMedicalLinkHostname } from '../api/apiConfig'
import type {
  ChatMessage,
  MailDocumentPayload,
  ReminderPreviewPayload,
  SecretaryMessagePayload,
  SportsNocCertificatePayload,
} from '../types/chat'
import {
  isPatientSwitchConfirmationMessage,
  patientSwitchConfirmIsActionable,
} from '../utils/patientSwitchConfirm'
import {
  isReadModalityConfirmationMessage,
  readModalityConfirmIsActionable,
  readModalityUiFrench,
} from '../utils/readModalityConfirm'
import {
  checkExamReceiptConfirmIsActionable,
  checkExamReceiptUiFrench,
  isCheckExamReceiptConfirmationMessage,
} from '../utils/checkExamReceiptConfirm'
import type { NormalizedUnreadMessage } from '../types/unreadMessages'
import type { RefObject, UIEvent, ReactNode } from 'react'
import { Fragment, useEffect, useId, useRef, useState } from 'react'
import { useInlineFieldSpeechRecognition } from '../hooks/useInlineFieldSpeechRecognition'
import { useWheelScrollIsolation } from '../hooks/useWheelScrollIsolation'
import {
  formatAssistantFooterTimeWithLatency,
  formatChatDateDisplay,
  formatInterMessageGapSeconds,
  formatTypedCommandFooterTime,
  formatUserFooterTimeWithTranscribe,
} from '../utils/formatChatDate'
import { chatContentString, chatContentTrim } from '../utils/chatContent'
import { reminderSuccessPreviewFromMessage } from '../utils/reminderPreviewInlineEdit'
import { isHistorySnapshot } from '../utils/chatHistoryPersistence'
import { isGlobalCancelNoticeContent } from '../utils/globalIntentCancel'
import { isSecretaryPreviewActionable } from '../utils/secretaryPreviewFollowupText'
import { secretaryPreviewFrFromMessage } from '../utils/secretaryMessagePreviewLocale'
import { getChatUiStrings, isFrenchLanguage } from '../utils/chatUiLocale'
import {
  ReminderPreviewInlineRows,
  type ReminderInlineVoiceTranscribeConfig,
  type ReminderPreviewEditField,
  type ReminderPreviewInlineEditProps,
} from './ReminderPreviewInlineEdit'
import {
  NoteTaskContentInlineEditRow,
  type NoteTaskPreviewInlineEditProps,
} from './NoteTaskPreviewInlineEdit'
import {
  SecretaryMessageInlineEditRow,
  type SecretaryMessageInlineEditField,
} from './SecretaryMessageInlineEdit'
import { mailSendConfirmLabels, mailSendConfirmQuestion } from '../utils/inlineMailConfirm'
import {
  sportsNocMailConfirmLabels,
  type SportsNocLocationOption,
  type SportsNocSportOption,
  type SportsNocSummaryEditField,
  type SportsNocYesNoKind,
} from '../utils/sportsNocFlow'
import {
  isRenewOrdonnanceMailConfirmRow,
  renewOrdonnanceAssistantBubbleText,
  renewOrdonnanceMailConfirmUiReady,
  renewOrdonnanceSuccessUiReady,
} from '../utils/renewOrdonnanceFlow'
import { prescriptionFriendlyLabel } from '../utils/prescriptionTypes'
import type { UnreadMessagesListProps } from './unread/UnreadMessagesList'
import { UnreadMessagesList } from './unread/UnreadMessagesList'
import { GetLastMessageList } from './getLastMessage/GetLastMessageList'

/** Guided sports NOC: metadata-only trigger (no standalone intro bubble). */
function isSportsNocSilentTrigger(m: ChatMessage): boolean {
  return m.role === 'assistant' && Boolean(m.sportsNocFlowTrigger && !chatContentTrim(m.content))
}

/** Same priority as `pickFirstIntentUrl` in intentNavigation.ts (first non-empty wins). */
function intentUrlToOpen(m: ChatMessage): string | undefined {
  return m.openPatientDossierUrl || m.openNavigationUrl
}

/** Secretary/message preview UI: French when subject+body are both French, else backend hint. */
function secretaryPreviewFr(m?: ChatMessage): boolean {
  return secretaryPreviewFrFromMessage(m ?? {})
}

function uiLangFr(): boolean {
  return isFrenchLanguage()
}

function mailMessageFrench(m: ChatMessage): boolean {
  if (m.mailFrench === true) return true
  if (m.mailFrench === false) return false
  const body = (m.mailDocument?.mailBody || '').trim()
  if (/\bci-?joint/i.test(body)) return true
  return uiLangFr()
}

function genderDisplay(value: string | undefined): string {
  const s = (value || '').trim().toLowerCase()
  if (!s) return '—'
  if (s === '1' || s === 'm' || s === 'male' || s === 'homme') return 'Homme'
  if (s === '2' || s === 'f' || s === 'female' || s === 'femme') return 'Femme'
  return (value || '').trim()
}

/** Strip http(s) URLs from assistant text so chat history never shows raw navigation links. */
function stripHttpUrls(s: string): string {
  return s.replace(/\s*https?:\/\/\S+/gi, ' ').replace(/\s+/g, ' ').trim()
}

/** Page navigation rows: friendly line from metadata, never the resolved MediKaLLink URL. */
function navigationChatBubbleText(m: ChatMessage): string {
  const nav = m.navigationMeta!
  if (nav.needs_clarification) {
    const p = nav.clarification_prompt?.trim()
    if (p) return p
    return stripHttpUrls(chatContentTrim(m.content)) || '—'
  }
  const label = nav.page_label?.trim()
  if (label) {
    return uiLangFr() ? `Ouverture de la page ${label}.` : `Opening ${label} page.`
  }
  const stripped = stripHttpUrls(chatContentTrim(m.content))
  if (stripped) return stripped
  return uiLangFr() ? 'Ouverture de la page.' : 'Opening page.'
}

/** Shared checkmark hero (reminder set successfully, mail sent successfully, …). */
function PipelineSuccessHero(props: { title: string; subtitle: string }) {
  const { title, subtitle } = props
  return (
    <div className="vnw-reminderSuccessHero">
      <div className="vnw-reminderSuccessHeroIcon" aria-hidden="true">
        <svg className="vnw-reminderSuccessHeroSvg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="11" className="vnw-reminderSuccessHeroSvgCircle" />
          <path
            className="vnw-reminderSuccessHeroSvgCheck"
            d="M7.2 12.3l2.7 2.7 6.9-6.9"
            fill="none"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div className="vnw-reminderSuccessHeroText">
        <div className="vnw-reminderSuccessHeroTitle">{title}</div>
        <div className="vnw-reminderSuccessHeroSub">{subtitle}</div>
      </div>
    </div>
  )
}

/** Information hero (info icon) — note/task cancelled and similar notices. */
function PipelineInfoHero(props: { title: string; subtitle?: string }) {
  return <PipelineCancelledHero {...props} />
}

/** Information hero for cancel / all-clear notices (reminder, sports NOC, global cancel, …). */
function PipelineCancelledHero(props: { title: string; subtitle?: string }) {
  const { title, subtitle } = props
  return (
    <div className="vnw-reminderCancelledHero">
      <div className="vnw-reminderCancelledHeroIcon" aria-hidden="true">
        <svg className="vnw-reminderCancelledHeroSvg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="11" className="vnw-reminderCancelledHeroSvgCircle" />
          <circle cx="12" cy="8.15" r="1.15" className="vnw-reminderCancelledHeroSvgInfoMark" />
          <path
            className="vnw-reminderCancelledHeroSvgInfoStem"
            d="M12 11.1v5.2"
            fill="none"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <div className="vnw-reminderCancelledHeroText">
        <div className="vnw-reminderCancelledHeroTitle">{title}</div>
        {subtitle ? <div className="vnw-reminderCancelledHeroSub">{subtitle}</div> : null}
      </div>
    </div>
  )
}

function shouldShowCancellationNotice(m: ChatMessage): boolean {
  if (m.cancelNotice) return true
  if (m.sportsNocFlowCancelled) return true
  // Sports NOC mail step: backend answers with plain text (kind="sports_noc")
  // when doctor chooses "no, do not send".
  // Render the same info/cancel hero UI instead of a plain bubble.
  const content = chatContentString(m.content)
  if (/(?:e-?mail)\s+(?:non\s+)?envoy[eé]?\b|email\s+not\s+sent\b/i.test(content)) return true
  if (m.reminderCancelled && !m.reminderPreview) return true
  if (m.secretaryMessage?.autofill_note === 'message_send_cancelled') return true
  return isGlobalCancelNoticeContent(chatContentString(m.content))
}

function CancellationNotice(props: { m: ChatMessage }) {
  const { m } = props
  const text = chatContentTrim(m.content)
  const fr = uiLangFr()
  const title = text || (fr ? 'Annulé' : 'Cancelled')
  return (
    <div className="vnw-cancelledEventShell" aria-label={title}>
      <PipelineCancelledHero title={title} />
    </div>
  )
}

function formatMailRecipientTypeLabel(recipientType: string, fr: boolean): string {
  const k = recipientType.trim().toLowerCase()
  if (k === 'patient') return 'Patient'
  if (k === 'doctor') return fr ? 'Médecin' : 'Doctor'
  if (k === 'secretary') return fr ? 'Secrétaire' : 'Secretary'
  return recipientType.trim() || '—'
}

function mailDocumentPreviewLabels(): {
  subject: string
  documentType: string
  recipientType: string
  recipient: string
  body: string
} {
  const fr = uiLangFr()
  return fr
    ? {
        subject: 'Objet',
        documentType: 'Type de document',
        recipientType: 'Type de destinataire',
        recipient: 'Destinataire',
        body: 'Message',
      }
    : {
        subject: 'Subject',
        documentType: 'Document type',
        recipientType: 'Recipient type',
        recipient: 'Recipient',
        body: 'Message',
      }
}

function MailDocumentPreviewCard(props: {
  mail: MailDocumentPayload
  sendConfirm?: { onYes: () => void; onNo: () => void }
  /** Sports NOC mail step: large Yes/No action cards inside the preview shell. */
  actionCardsConfirm?: { onYes: () => void; onNo: () => void; yesLabel: string; noLabel: string; ariaLabel: string }
  isSending?: boolean
  fr?: boolean
  previewActions?: {
    onEditSubject: () => void
    onEditMessage: () => void
    onEditRecipientName: () => void
  }
  inline?: {
    field: 'subject' | 'message' | 'recipient_name' | null
    draft: string
    saving: boolean
    error: string | null
    assigneeOptions?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
    onAssigneeOptionSelect?: (selectionId: string) => void
  }
}) {
  const { mail, sendConfirm, actionCardsConfirm, isSending, fr: frProp, previewActions, inline } = props
  const fr = frProp ?? uiLangFr()
  const L = mailDocumentPreviewLabels()
  const editLabel = fr ? 'Modifier' : 'Edit'
  const subject = (mail.mailSubject || mail.documentType || '').trim() || '—'
  const docType = (mail.documentType || '').trim() || '—'
  const recipientType = formatMailRecipientTypeLabel(mail.recipientType || '', fr)
  const recipient = (mail.recipientName || '').trim() || '—'
  const body =
    (mail.mailBody || '').trim() ||
    (docType !== '—' ? (fr ? `${docType} ci-joint.` : `${docType} enclosed.`) : '—')
  const confirmLabels = mailSendConfirmLabels(fr)
  const editing = Boolean(inline?.field)
  const showEditActions = Boolean(previewActions && !editing)
  return (
    <div
      className="vnw-mailDocumentPreviewShell vnw-secretaryMessagePreview vnw-mailDocumentPreview"
      aria-label={fr ? 'Aperçu du courriel' : 'Mail preview'}
    >
      {inline?.field === 'subject' ? (
        <SecretaryMessageInlineEditRow
          field="purpose"
          fr={fr}
          draft={inline.draft}
          saving={inline.saving}
          error={inline.error}
          assigneeOptions={inline.assigneeOptions}
          voiceTranscribe={inline.voiceTranscribe}
          onDraftChange={inline.onDraftChange}
          onSave={inline.onSave}
          onCancel={inline.onCancel}
          onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
        />
      ) : (
        <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--purpose">
          <span className="vnw-secretaryMessagePreviewLabel">{L.subject}</span>
          <span className="vnw-secretaryMessagePreviewValue vnw-secretaryMessagePreviewValue--emphasis">
            {subject}
          </span>
          <span className="vnw-sportsNocKvAction">
            {showEditActions ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={previewActions!.onEditSubject}>
                {editLabel}
              </button>
            ) : null}
          </span>
        </div>
      )}
      <div className="vnw-secretaryMessagePreviewRow">
        <span className="vnw-secretaryMessagePreviewLabel">{L.documentType}</span>
        <span className="vnw-secretaryMessagePreviewValue">{docType}</span>
        <span className="vnw-sportsNocKvAction" aria-hidden="true" />
      </div>
      <div className="vnw-secretaryMessagePreviewRow">
        <span className="vnw-secretaryMessagePreviewLabel">{L.recipientType}</span>
        <span className="vnw-secretaryMessagePreviewValue">{recipientType}</span>
        <span className="vnw-sportsNocKvAction" aria-hidden="true" />
      </div>
      {inline?.field === 'recipient_name' ? (
        <SecretaryMessageInlineEditRow
          field="recipient_name"
          fr={fr}
          draft={inline.draft}
          saving={inline.saving}
          error={inline.error}
          assigneeOptions={inline.assigneeOptions}
          voiceTranscribe={inline.voiceTranscribe}
          onDraftChange={inline.onDraftChange}
          onSave={inline.onSave}
          onCancel={inline.onCancel}
          onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
        />
      ) : (
        <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--recipient">
          <span className="vnw-secretaryMessagePreviewLabel">{L.recipient}</span>
          <span className="vnw-secretaryMessagePreviewValue vnw-secretaryMessagePreviewValue--emphasis">
            {recipient}
          </span>
          <span className="vnw-sportsNocKvAction">
            {showEditActions ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={previewActions!.onEditRecipientName}>
                {editLabel}
              </button>
            ) : null}
          </span>
        </div>
      )}
      {inline?.field === 'message' ? (
        <SecretaryMessageInlineEditRow
          field="message"
          fr={fr}
          draft={inline.draft}
          saving={inline.saving}
          error={inline.error}
          assigneeOptions={inline.assigneeOptions}
          voiceTranscribe={inline.voiceTranscribe}
          onDraftChange={inline.onDraftChange}
          onSave={inline.onSave}
          onCancel={inline.onCancel}
          onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
        />
      ) : (
        <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--body">
          <span className="vnw-secretaryMessagePreviewLabel">{L.body}</span>
          <span className="vnw-secretaryMessagePreviewValue">{body}</span>
          <span className="vnw-sportsNocKvAction">
            {showEditActions ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={previewActions!.onEditMessage}>
                {editLabel}
              </button>
            ) : null}
          </span>
        </div>
      )}
      {actionCardsConfirm ? (
        <>
          <p className="vnw-secretaryMessageSendQuestion">{mailSendConfirmQuestion(fr)}</p>
          <SportsNocGenerateConfirmCards
            fr={fr}
            isSending={isSending}
            onYes={actionCardsConfirm.onYes}
            onNo={actionCardsConfirm.onNo}
            yesLabel={actionCardsConfirm.yesLabel}
            noLabel={actionCardsConfirm.noLabel}
            ariaLabel={actionCardsConfirm.ariaLabel}
          />
        </>
      ) : sendConfirm ? (
        <>
          <p className="vnw-secretaryMessageSendQuestion">{mailSendConfirmQuestion(fr)}</p>
          <div
            className="vnw-sportsNocYesNo vnw-secretaryMessageYesNo vnw-mailDocumentPreviewYesNo"
            role="group"
            aria-label={fr ? 'Confirmer l’envoi du courriel' : 'Confirm sending this email'}
          >
            <button
              type="button"
              className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
              disabled={Boolean(isSending)}
              onClick={sendConfirm.onYes}
            >
              {confirmLabels.yes}
            </button>
            <button
              type="button"
              className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
              disabled={Boolean(isSending)}
              onClick={sendConfirm.onNo}
            >
              {confirmLabels.no}
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

function mailDocumentSentSuccess(mail: MailDocumentPayload): boolean {
  const st = String(mail.mail_sent_status || '').trim().toLowerCase()
  return st === 'success' || st === 'sent'
}

function MailDocumentSentSuccessCard(props: { mail: MailDocumentPayload; fr?: boolean }) {
  const { mail, fr: frProp } = props
  const fr = frProp ?? uiLangFr()
  const name = (mail.recipientName || '').trim()
  const title = name
    ? fr
      ? `E-mail envoyé avec succès à ${name}.`
      : `Mail sent successfully to ${name}.`
    : fr
      ? 'E-mail envoyé avec succès.'
      : 'Mail sent successfully.'
  const doc = (mail.documentType || '').toLowerCase()
  const isRenewOrdonnance = doc.includes('vidal') || doc.includes('ordonnance')
  const subtitle = name
    ? isRenewOrdonnance
      ? fr
        ? "L'ordonnance renouvelée et le message ont été envoyés au patient."
        : 'The renewed ordonnance and message were sent to the patient.'
      : fr
        ? 'Le certificat et le message ont été envoyés au patient.'
        : 'The certificate and message were sent to the patient.'
    : fr
      ? 'Le message et la pièce jointe ont été envoyés.'
      : 'Your message and attachment were sent.'
  return (
    <div
      className="vnw-mailDocumentSuccessShell vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={title}
    >
      <PipelineSuccessHero title={title} subtitle={subtitle} />
    </div>
  )
}

function recipientTypeLabel(t: string, fr: boolean): string {
  const key = (t || '').trim().toLowerCase()
  const en: Record<string, string> = {
    secretary: 'Secretary',
    doctor: 'Doctor',
    patient: 'Patient',
  }
  const frMap: Record<string, string> = {
    secretary: 'Secrétaire',
    doctor: 'Médecin',
    patient: 'Patient',
  }
  const map = fr ? frMap : en
  return map[key] || (key ? key : '—')
}

function secretaryMessagePreviewLabels(fr: boolean) {
  return fr
    ? {
        purpose: 'Objet',
        message: 'Message',
        recipient: 'Nom du destinataire',
        recipientType: 'Type',
        urgent: 'Urgence',
        urgentOn: 'Urgent',
        urgentOff: 'Non urgent',
        markUrgent: 'Marquer urgent',
        clearUrgent: "Retirer l'urgence",
        mentionedInMessage: 'Personne citée dans le message',
        error: 'Erreur',
      }
    : {
        purpose: 'Subject',
        message: 'Message',
        recipient: 'Recipient name',
        recipientType: 'Recipient type',
        urgent: 'Urgent',
        urgentOn: 'Urgent',
        urgentOff: 'Not urgent',
        markUrgent: 'Mark urgent',
        clearUrgent: 'Clear urgent',
        mentionedInMessage: 'Referenced in message',
        error: 'Error',
      }
}

function mentionedContactPreviewValue(sm: SecretaryMessagePayload, fr: boolean): string | null {
  const role = sm.mentioned_contact_role
  const name = (sm.mentioned_contact_name || '').trim()
  if (!role || !name) {
    return null
  }
  if (role === 'secretary') {
    return fr ? `Secrétaire : ${name}` : `Secretary: ${name}`
  }
  return fr ? `Médecin : ${name}` : `Doctor: ${name}`
}

function SecretaryMessageSentSuccessCard(props: { sm: SecretaryMessagePayload; fr?: boolean }) {
  const { sm, fr: frProp } = props
  const fr = frProp ?? secretaryPreviewFrFromMessage({ secretaryMessage: sm })
  const whom = sm.whom
  const recipientName =
    typeof whom === 'object' && whom !== null
      ? (whom.name || '').trim() || (sm.secretary_name || '').trim()
      : (sm.secretary_name || '').trim()
  const recipientTypeRaw =
    typeof whom === 'object' && whom !== null ? (whom.type || '').trim() : typeof whom === 'string' ? whom : ''
  const typeDisp = recipientTypeLabel(recipientTypeRaw, fr)
  const title = fr ? 'Message envoyé avec succès.' : 'Message sent successfully.'
  const subtitle = recipientName
    ? fr
      ? `Envoyé à ${recipientName}, ${typeDisp}.`
      : `Sent to ${recipientName}, ${typeDisp}.`
    : fr
      ? 'Votre message a été envoyé.'
      : 'Your message was sent.'
  return (
    <div
      className="vnw-secretaryMessageSuccessShell vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={title}
    >
      <PipelineSuccessHero title={title} subtitle={subtitle} />
    </div>
  )
}

function sportsNocUiFr(m?: ChatMessage): boolean {
  if (m?.sportsNocFrench === true) return true
  if (m?.sportsNocFrench === false) return false
  return uiLangFr()
}

function promptTextBeforeNumberedOptions(m: ChatMessage): string {
  const raw = chatContentTrim(m.content)
  if (!raw) return ''
  const lines = raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
  const promptLines: string[] = []
  for (const line of lines) {
    if (/^\d+\.\s/.test(line)) break
    promptLines.push(line)
  }
  return promptLines.join('\n').trim() || raw
}

function assigneeOptionsPromptText(m: ChatMessage): string {
  return promptTextBeforeNumberedOptions(m)
}

function prescriptionTypeOptionsPromptText(m: ChatMessage): string {
  return promptTextBeforeNumberedOptions(m)
}

function SportsCertificateGeneratedSuccessCard(props: { fr: boolean }) {
  const { fr } = props
  return (
    <div
      className="vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={fr ? 'Certificat généré avec succès' : 'Sports certificate generated successfully'}
    >
      <PipelineSuccessHero
        title={fr ? 'Certificat généré avec succès' : 'Sports certificate generated successfully'}
        subtitle={
          fr
            ? 'Le certificat est prêt — consultez l’aperçu du document.'
            : 'Your certificate is ready — see the document preview.'
        }
      />
    </div>
  )
}

function renewOrdonnanceUiFr(m?: ChatMessage): boolean {
  if (m?.renewOrdonnanceFrench === true) return true
  if (m?.renewOrdonnanceFrench === false) return false
  return uiLangFr()
}

function RenewOrdonnanceSuccessCard(props: { fr: boolean }) {
  const { fr } = props
  return (
    <div
      className="vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={fr ? 'Ordonnance renouvelée avec succès' : 'Vidal ordonnance renewed successfully'}
    >
      <PipelineSuccessHero
        title={fr ? 'Ordonnance Vidal renouvelée avec succès' : 'Vidal ordonnance renewed successfully'}
        subtitle={
          fr
            ? "L'ordonnance renouvelée est affichée dans l'aperçu du document."
            : 'The renewed ordonnance is shown in the document preview.'
        }
      />
    </div>
  )
}

function isSportsNocMailConfirmRow(m: ChatMessage): boolean {
  return (
    m.sportsNocYesNoPrompt?.kind === 'mail_certificate' &&
    Boolean(m.mailDocument?.awaitingMailConfirmation || m.awaitingMailConfirmation)
  )
}

function mailPreviewIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist) return false
  if (m.sportsNocYesNoPrompt?.kind === 'mail_certificate') return false
  return Boolean(m.mailDocument?.awaitingMailConfirmation || m.awaitingMailConfirmation)
}

function isMailDocumentPreviewEditable(m: ChatMessage, hist: boolean): boolean {
  if (hist) return false
  return Boolean(m.mailDocument?.awaitingMailConfirmation || m.awaitingMailConfirmation)
}

function mailDocumentPreviewCardExtras(
  m: ChatMessage,
  hist: boolean,
  inlineEdit: MessageListProps['mailDocumentPreviewInlineEdit'],
  onMailPreviewEdit?: MessageListProps['onMailPreviewEdit'],
): Pick<Parameters<typeof MailDocumentPreviewCard>[0], 'previewActions' | 'inline'> {
  if (!isMailDocumentPreviewEditable(m, hist) || !onMailPreviewEdit) return {}
  const previewActions = {
    onEditSubject: () => onMailPreviewEdit(m.id, 'edit_subject'),
    onEditMessage: () => onMailPreviewEdit(m.id, 'edit_message'),
    onEditRecipientName: () => onMailPreviewEdit(m.id, 'edit_recipient_name'),
  }
  if (!inlineEdit || inlineEdit.messageId !== m.id || !inlineEdit.field) {
    return { previewActions }
  }
  return {
    previewActions,
    inline: {
      field: inlineEdit.field,
      draft: inlineEdit.draft,
      saving: inlineEdit.saving,
      error: inlineEdit.error,
      assigneeOptions: inlineEdit.assigneeOptions,
      voiceTranscribe: inlineEdit.voiceTranscribe,
      onDraftChange: inlineEdit.onDraftChange,
      onSave: inlineEdit.onSave,
      onCancel: inlineEdit.onCancel,
      onAssigneeOptionSelect: inlineEdit.onAssigneeOptionSelect,
    },
  }
}

function isSportsNocGenerateConfirmRow(m: ChatMessage): boolean {
  if (m.sportsNocFlowCancelled) return false
  return Boolean(
    m.sportsNocGenerateConfirm ||
      (m.sportsNocAwaitingField === 'generate_confirm' &&
        m.sportsNocSummary &&
        m.sportsNocYesNoPrompt?.kind === 'generate_certificate'),
  )
}

export type SportsNocSummaryInlineEditProps = {
  messageId: string
  field: SportsNocSummaryEditField
  locationOptions: SportsNocLocationOption[]
  sportOptions: SportsNocSportOption[]
  pendingSportIds: number[]
  locationDraftId?: number
  commentsDraft: string
  saving: boolean
  error?: string | null
  isSending?: boolean
  onLocationDraftChange: (locationId: number) => void
  onSportToggle: (fieldId: number) => void
  onCommentsDraftChange: (text: string) => void
  onSave: () => void
  onCancel: () => void
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
}

function SportsNocCommentsInlineEditField(props: {
  fr: boolean
  draft: string
  saving: boolean
  disabled: boolean
  voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
  onDraftChange: (text: string) => void
}) {
  const { fr, draft, saving, disabled, voiceTranscribe, onDraftChange } = props
  const textareaId = useId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { supported, status, statusHint, error: speechError, toggle } = useInlineFieldSpeechRecognition({
    websocketUrl: voiceTranscribe?.websocketUrl ?? '',
    apiBaseUrl: voiceTranscribe?.apiBaseUrl,
    transcribeMode: voiceTranscribe?.transcribeMode,
    getMedicalLinkCredentials: voiceTranscribe?.getMedicalLinkCredentials,
    clientId: voiceTranscribe?.clientId,
    tenantId: voiceTranscribe?.tenantId,
    sessionId: voiceTranscribe?.sessionId,
    disabled: saving || disabled,
    blocked: voiceTranscribe?.blocked,
    onBusyChange: voiceTranscribe?.onBusyChange,
    onTranscript: (text) => onDraftChange(text),
  })

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  const helper =
    speechError ||
    (status === 'listening' && statusHint.trim()
      ? statusHint.trim()
      : fr
        ? 'Appuyez sur le micro pour parler ou saisissez votre texte.'
        : 'Tap mic to speak or type to edit.')
  const micLabel =
    status === 'listening'
      ? fr
        ? 'Écoute en cours'
        : 'Listening'
      : fr
        ? 'Dicter les commentaires'
        : 'Dictate comments'

  return (
    <>
      <label className="vnw-reminderInlineEditLabel" htmlFor={textareaId}>
        {fr ? 'Modifier les commentaires' : 'Edit comments'}
      </label>
      <div className="vnw-reminderInlineEditInputWrap vnw-sportsNocCommentsInlineWrap">
        <textarea
          id={textareaId}
          ref={textareaRef}
          className="vnw-sportsNocCommentsTextarea vnw-sportsNocCommentsTextarea--inline"
          rows={3}
          disabled={saving || disabled}
          placeholder={fr ? 'Saisissez ici ou utilisez le micro…' : 'Type here or use the microphone…'}
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
        />
        <button
          type="button"
          className={
            'vnw-reminderInlineEditMic' + (status === 'listening' ? ' vnw-reminderInlineEditMic--active' : '')
          }
          aria-label={micLabel}
          title={supported ? micLabel : fr ? 'Saisie vocale indisponible' : 'Voice input unavailable'}
          disabled={saving || disabled || !supported}
          aria-pressed={status === 'listening'}
          onClick={() => toggle()}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              fill="currentColor"
              d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.93V21h2v-3.07A7 7 0 0 0 19 11h-2Z"
            />
          </svg>
        </button>
      </div>
      <p className="vnw-reminderInlineEditHelper" role="status">
        {helper}
      </p>
    </>
  )
}

function SportsNocSummaryCard(props: {
  summary: SportsNocCertificatePayload
  fr?: boolean
  generatePreview?: boolean
  actions?: {
    onEditLocation?: () => void
    onEditSports: () => void
    onEditComments: () => void
  }
  inlineEdit?: SportsNocSummaryInlineEditProps | null
}) {
  const { summary, actions, generatePreview, fr: frProp, inlineEdit } = props
  const fr = frProp ?? uiLangFr()
  const editingField = inlineEdit?.field
  const sportsLine = (summary.sports ?? []).length
    ? (summary.sports ?? []).map((s) => (s || '').trim()).join(', ')
    : '—'
  const commentsLine =
    summary.comments == null || !String(summary.comments).trim()
      ? fr
        ? 'Aucun'
        : 'None'
      : String(summary.comments).trim()
  const locationLine = (summary.locationName || '').trim() || '—'
  const patientLine = (summary.patient.name || '').trim() || '—'
  const editLabel = fr ? 'Modifier' : 'Edit'
  const inlineBusy = Boolean(inlineEdit?.saving || inlineEdit?.isSending)
  const selectedSportSet = new Set(inlineEdit?.pendingSportIds ?? [])

  const inlineActions = inlineEdit ? (
    <div className="vnw-reminderInlineEditActions vnw-sportsNocSummaryInlineActions">
      <button
        type="button"
        className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--primary"
        disabled={inlineBusy}
        onClick={inlineEdit.onSave}
      >
        {fr ? 'Enregistrer' : 'Save'}
      </button>
      <button
        type="button"
        className="vnw-reminderInlineEditBtn vnw-reminderInlineEditBtn--ghost"
        disabled={inlineBusy}
        onClick={inlineEdit.onCancel}
      >
        {fr ? 'Annuler' : 'Cancel'}
      </button>
    </div>
  ) : null

  const locationDraftValue =
    inlineEdit?.locationDraftId && inlineEdit.locationDraftId > 0
      ? String(inlineEdit.locationDraftId)
      : ''

  return (
    <div
      className={`vnw-secretaryMessagePreview vnw-sportsNocKvSummary${generatePreview ? ' vnw-sportsNocKvSummary--generatePreview' : ''}${editingField ? ' vnw-sportsNocKvSummary--editing' : ''}`}
      aria-label={fr ? 'Aperçu certificat sport' : 'Sports certificate preview'}
    >
      {generatePreview ? (
        <div className="vnw-sportsNocPreviewTitle">
          {fr ? 'Aperçu des informations' : 'Collected information preview'}
        </div>
      ) : null}
      <div className="vnw-sportsNocKvCard" role="list">
        <div className="vnw-sportsNocKvRow" role="listitem">
          <div className="vnw-sportsNocKvKey">{fr ? 'Patient' : 'Patient'}</div>
          <div className="vnw-sportsNocKvVal">{patientLine}</div>
          <div className="vnw-sportsNocKvAction" aria-hidden="true" />
        </div>
        {editingField === 'location' && inlineEdit ? (
          <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Localisation' : 'Location'}</div>
            <div className="vnw-reminderInlineEditBody vnw-sportsNocSummaryInlineBody">
              <label className="vnw-sportsNocLocationDropdownLabel" htmlFor="vnw-sports-noc-summary-location">
                {fr ? 'Modifier la localisation' : 'Edit location'}
              </label>
              <select
                id="vnw-sports-noc-summary-location"
                className="vnw-sportsNocLocationDropdown"
                disabled={inlineBusy}
                value={locationDraftValue}
                onChange={(e) => {
                  const next = Number(e.target.value)
                  if (Number.isFinite(next) && next > 0) inlineEdit.onLocationDraftChange(next)
                }}
              >
                <option value="">{fr ? '— Choisir —' : '— Select —'}</option>
                {inlineEdit.locationOptions.map((opt) => (
                  <option key={opt.locationId} value={opt.locationId}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {inlineEdit.error ? (
                <p className="vnw-reminderInlineEditHelper" role="alert">
                  {inlineEdit.error}
                </p>
              ) : null}
              {inlineActions}
            </div>
          </div>
        ) : (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Localisation' : 'Location'}</div>
            <div className="vnw-sportsNocKvVal">{locationLine}</div>
            <div className="vnw-sportsNocKvAction">
              {actions?.onEditLocation && !editingField ? (
                <button type="button" className="vnw-sportsNocKvEdit" onClick={() => actions.onEditLocation!()}>
                  {editLabel}
                </button>
              ) : null}
            </div>
          </div>
        )}
        {editingField === 'sports' && inlineEdit ? (
          <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Sports' : 'Sports'}</div>
            <div className="vnw-reminderInlineEditBody vnw-sportsNocSummaryInlineBody">
              <div className="vnw-sportsNocSummaryInlineSports" role="group">
                {inlineEdit.sportOptions.map((opt) => {
                  const ticked = selectedSportSet.has(opt.fieldId)
                  return (
                    <button
                      key={opt.fieldId}
                      type="button"
                      className={
                        ticked
                          ? 'vnw-sportsNocSportOptionBtn vnw-sportsNocSportOptionBtn--selected vnw-sportsNocSummaryInlineSportBtn'
                          : 'vnw-sportsNocSportOptionBtn vnw-sportsNocSummaryInlineSportBtn'
                      }
                      disabled={inlineBusy}
                      aria-pressed={ticked}
                      onClick={() => inlineEdit.onSportToggle(opt.fieldId)}
                    >
                      <span className="vnw-sportsNocSportOptionTick" aria-hidden>
                        {ticked ? '✓' : ''}
                      </span>
                      <span className="vnw-sportsNocSportOptionLabel">{opt.label}</span>
                    </button>
                  )
                })}
              </div>
              {inlineEdit.error ? (
                <p className="vnw-reminderInlineEditHelper" role="alert">
                  {inlineEdit.error}
                </p>
              ) : null}
              {inlineActions}
            </div>
          </div>
        ) : (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Sports' : 'Sports'}</div>
            <div className="vnw-sportsNocKvVal">{sportsLine}</div>
            <div className="vnw-sportsNocKvAction">
              {actions && !editingField ? (
                <button type="button" className="vnw-sportsNocKvEdit" onClick={() => actions.onEditSports()}>
                  {editLabel}
                </button>
              ) : null}
            </div>
          </div>
        )}
        {editingField === 'comments' && inlineEdit ? (
          <div className="vnw-sportsNocKvRow vnw-reminderInlineEditRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Commentaires' : 'Comments'}</div>
            <div className="vnw-reminderInlineEditBody vnw-sportsNocSummaryInlineBody">
              <SportsNocCommentsInlineEditField
                fr={fr}
                draft={inlineEdit.commentsDraft}
                saving={Boolean(inlineEdit.saving)}
                disabled={inlineBusy}
                voiceTranscribe={inlineEdit.voiceTranscribe}
                onDraftChange={inlineEdit.onCommentsDraftChange}
              />
              {inlineEdit.error ? (
                <p className="vnw-reminderInlineEditHelper" role="alert">
                  {inlineEdit.error}
                </p>
              ) : null}
              {inlineActions}
            </div>
          </div>
        ) : (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Commentaires' : 'Comments'}</div>
            <div className="vnw-sportsNocKvVal vnw-sportsNocKvVal--multiline">{commentsLine}</div>
            <div className="vnw-sportsNocKvAction">
              {actions && !editingField ? (
                <button type="button" className="vnw-sportsNocKvEdit" onClick={() => actions.onEditComments()}>
                  {editLabel}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function isSportsNocOptionPickerMessage(m: ChatMessage): boolean {
  return (
    m.uiType === 'sports_noc_location_options' ||
    m.uiType === 'sports_noc_sports_options' ||
    m.uiType === 'sports_noc_comments_input' ||
    // Comments prompt step may be delivered as a plain assistant bubble (no uiType),
    // while the guided flow sets `sportsNocAwaitingField = 'comments'`.
    (m.sportsNocNeedsClarification === true &&
      m.sportsNocAwaitingField === 'comments' &&
      m.uiType !== 'sports_noc_comments_input')
  )
}

function SportsNocSportsOptionCards(props: {
  messageId: string
  options: readonly { fieldId: number; label: string }[]
  selectedIds: readonly number[]
  fr: boolean
  resolved?: boolean
  hist?: boolean
  isSending?: boolean
  onToggle?: (messageId: string, fieldId: number) => void
  onConfirm?: (messageId: string) => void
}) {
  const { messageId, options, selectedIds, fr, resolved, hist, isSending, onToggle, onConfirm } = props
  const selected = new Set(selectedIds)
  const disabled = Boolean(isSending) || Boolean(resolved) || hist
  return (
    <div
      className={
        resolved || hist
          ? 'vnw-sportsNocSportOptions vnw-sportsNocSportOptions--resolved'
          : 'vnw-sportsNocSportOptions'
      }
      role="group"
      aria-label={
        resolved || hist
          ? fr
            ? 'Choix de sports (confirmé)'
            : 'Sports choices (confirmed)'
          : fr
            ? 'Choisir un ou plusieurs sports'
            : 'Choose one or more sports'
      }
    >
      {resolved || hist ? (
        <div className="vnw-sportsNocSportOptionsResolvedNote" aria-hidden>
          {fr ? 'Sports choisis — cartes conservées' : 'Sports selected — cards kept for context'}
        </div>
      ) : null}
      {options.map((opt, index) => {
        const ticked = selected.has(opt.fieldId)
        return (
          <button
            key={opt.fieldId}
            type="button"
            className={
              ticked
                ? 'vnw-sportsNocSportOptionBtn vnw-sportsNocSportOptionBtn--selected'
                : 'vnw-sportsNocSportOptionBtn'
            }
            disabled={disabled}
            aria-pressed={ticked}
            aria-label={`${index + 1}. ${opt.label}`}
            onClick={() => onToggle?.(messageId, opt.fieldId)}
          >
            <span className="vnw-sportsNocSportOptionIndex" aria-hidden>
              {index + 1}
            </span>
            <span className="vnw-sportsNocSportOptionTick" aria-hidden>
              {ticked ? '✓' : ''}
            </span>
            <span className="vnw-sportsNocSportOptionLabel">{opt.label}</span>
          </button>
        )
      })}
      {!resolved && !hist ? (
        <button
          type="button"
          className="vnw-sportsNocSportOptionsConfirm"
          disabled={disabled || selected.size === 0}
          onClick={() => onConfirm?.(messageId)}
        >
          {fr ? 'Continuer' : 'Continue'}
        </button>
      ) : null}
    </div>
  )
}

function SportsNocLocationOptionCards(props: {
  options: readonly { locationId: number; label: string }[]
  fr: boolean
  resolved?: boolean
  hist?: boolean
  isSending?: boolean
  onSelect?: (locationId: number) => void
}) {
  const { options, fr, resolved, hist, isSending, onSelect } = props
  return (
    <div
      className={
        resolved || hist
          ? 'vnw-sportsNocLocationOptions vnw-sportsNocLocationOptions--resolved'
          : 'vnw-sportsNocLocationOptions'
      }
      role="group"
      aria-label={
        resolved || hist
          ? fr
            ? 'Choix de localisation (confirmé)'
            : 'Location choices (confirmed)'
          : fr
            ? 'Choisir une localisation'
            : 'Choose a location'
      }
    >
      {resolved || hist ? (
        <div className="vnw-sportsNocLocationOptionsResolvedNote" aria-hidden>
          {fr ? 'Localisation choisie — cartes conservées' : 'Location selected — cards kept for context'}
        </div>
      ) : null}
      {options.map((opt, index) => (
        <button
          key={opt.locationId}
          type="button"
          className="vnw-sportsNocLocationOptionBtn"
          disabled={Boolean(isSending) || Boolean(resolved) || hist}
          aria-label={`${index + 1}. ${opt.label}`}
          onClick={() => onSelect?.(opt.locationId)}
        >
          <span className="vnw-sportsNocLocationOptionIndex" aria-hidden>
            {index + 1}
          </span>
          <span className="vnw-sportsNocLocationOptionLabel">{opt.label}</span>
        </button>
      ))}
    </div>
  )
}

function SportsNocLocationDropdown(props: {
  options: readonly { locationId: number; label: string }[]
  currentLocationId?: number
  fr: boolean
  isSending?: boolean
  onSelect?: (locationId: number) => void
}) {
  const { options, currentLocationId, fr, isSending, onSelect } = props
  const [value, setValue] = useState(() =>
    currentLocationId && currentLocationId > 0 ? String(currentLocationId) : '',
  )
  useEffect(() => {
    if (currentLocationId && currentLocationId > 0) {
      setValue(String(currentLocationId))
    }
  }, [currentLocationId])
  return (
    <div className="vnw-sportsNocLocationDropdownWrap">
      <label className="vnw-sportsNocLocationDropdownLabel" htmlFor="vnw-sports-noc-location-select">
        {fr ? 'Localisation' : 'Location'}
      </label>
      <select
        id="vnw-sports-noc-location-select"
        className="vnw-sportsNocLocationDropdown"
        disabled={Boolean(isSending)}
        value={value}
        onChange={(e) => {
          const next = Number(e.target.value)
          setValue(e.target.value)
          if (Number.isFinite(next) && next > 0) onSelect?.(next)
        }}
      >
        <option value="">{fr ? '— Choisir —' : '— Select —'}</option>
        {options.map((opt) => (
          <option key={opt.locationId} value={opt.locationId}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

function SportsNocCommentsInput(props: {
  initialDraft?: string
  fr: boolean
  isSending?: boolean
  onSubmit?: (text: string) => void
}) {
  const { initialDraft, fr, isSending, onSubmit } = props
  const [draft, setDraft] = useState(initialDraft ?? '')
  useEffect(() => {
    setDraft(initialDraft ?? '')
  }, [initialDraft])
  return (
    <div className="vnw-sportsNocCommentsInput">
      <label className="vnw-sportsNocCommentsInputLabel" htmlFor="vnw-sports-noc-comments">
        {fr ? 'Commentaires' : 'Comments'}
      </label>
      <textarea
        id="vnw-sports-noc-comments"
        className="vnw-sportsNocCommentsTextarea"
        rows={3}
        disabled={Boolean(isSending)}
        placeholder={fr ? 'Saisissez ici ou utilisez le micro…' : 'Type here or use the microphone…'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="vnw-sportsNocCommentsActions">
        <button
          type="button"
          className="vnw-sportsNocCommentsSave"
          disabled={Boolean(isSending)}
          onClick={() => onSubmit?.(draft.trim() || (fr ? 'non' : 'no'))}
        >
          {fr ? 'Enregistrer' : 'Save'}
        </button>
        <button
          type="button"
          className="vnw-sportsNocCommentsNoBtn"
          disabled={Boolean(isSending)}
          onClick={() => {
            setDraft('')
            onSubmit?.(fr ? 'non' : 'no')
          }}
        >
          {fr ? 'Aucun commentaire' : 'No comments'}
        </button>
      </div>
    </div>
  )
}

function SportsNocGenerateConfirmCards(props: {
  fr: boolean
  isSending?: boolean
  onYes: () => void
  onNo: () => void
  yesLabel?: string
  noLabel?: string
  ariaLabel?: string
}) {
  const { fr, isSending, onYes, onNo, yesLabel, noLabel, ariaLabel } = props
  return (
    <div
      className="vnw-sportsNocActionCards"
      role="group"
      aria-label={
        ariaLabel ?? (fr ? 'Générer le certificat ?' : 'Generate sports certificate?')
      }
    >
      <button
        type="button"
        className="vnw-sportsNocActionCard vnw-sportsNocActionCard--yes"
        disabled={Boolean(isSending)}
        onClick={onYes}
      >
        {yesLabel ?? (fr ? 'Oui, générer' : 'Yes, generate')}
      </button>
      <button
        type="button"
        className="vnw-sportsNocActionCard vnw-sportsNocActionCard--no"
        disabled={Boolean(isSending)}
        onClick={onNo}
      >
        {noLabel ?? (fr ? 'Non, annuler' : 'No, cancel')}
      </button>
    </div>
  )
}

function ReadModalityYesNo(props: {
  fr: boolean
  isSending?: boolean
  yesNoDisabled?: boolean
  onYes: () => void
  onNo: () => void
}) {
  const { fr, isSending, yesNoDisabled, onYes, onNo } = props
  return (
    <div
      className="vnw-sportsNocYesNo vnw-reminderYesNo"
      role="group"
      aria-label={fr ? 'Confirmer la lecture à voix haute' : 'Confirm read aloud'}
    >
      <button
        type="button"
        className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
        disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
        onClick={onYes}
      >
        {fr ? 'Oui, continuer' : 'Yes, proceed'}
      </button>
      <button
        type="button"
        className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
        disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
        onClick={onNo}
      >
        {fr ? 'Non, annuler' : 'No, cancel'}
      </button>
    </div>
  )
}

function CheckExamReceiptConfirmCard(props: {
  m: ChatMessage
  fr: boolean
  isSending?: boolean
  yesNoDisabled?: boolean
  onYes: () => void
  onNo: () => void
}) {
  const { m, fr, isSending, yesNoDisabled, onYes, onNo } = props
  const prompt = m.checkExamReceiptYesNoPrompt
  const modality = (prompt?.modality || '').trim()
  const examDate = (prompt?.examDate || '').trim()
  return (
    <div
      className="vnw-secretaryMessagePreview vnw-sportsNocKvSummary"
      aria-label={fr ? 'Confirmation ouverture examen' : 'Exam open confirmation'}
    >
      <div className="vnw-sportsNocKvCard" role="list">
        {modality ? (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <span className="vnw-sportsNocKvKey">{fr ? 'Examen' : 'Exam'}</span>
            <span className="vnw-sportsNocKvVal">{modality}</span>
          </div>
        ) : null}
        {examDate ? (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <span className="vnw-sportsNocKvKey">{fr ? 'Date' : 'Date'}</span>
            <span className="vnw-sportsNocKvVal">{examDate}</span>
          </div>
        ) : null}
      </div>
      <div
        className="vnw-sportsNocYesNo vnw-reminderYesNo"
        role="group"
        aria-label={fr ? 'Confirmer ouverture' : 'Confirm open'}
      >
        <button
          type="button"
          className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
          disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
          onClick={onYes}
        >
          {fr ? 'Oui, ouvrir' : 'Yes, open'}
        </button>
        <button
          type="button"
          className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
          disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
          onClick={onNo}
        >
          {fr ? 'Non, annuler' : 'No, cancel'}
        </button>
      </div>
    </div>
  )
}

function PatientSwitchYesNo(props: {
  isSending?: boolean
  yesNoDisabled?: boolean
  onYes: () => void
  onNo: () => void
}) {
  const { isSending, yesNoDisabled, onYes, onNo } = props
  const fr = uiLangFr()
  return (
    <div
      className="vnw-sportsNocYesNo vnw-patientSwitchYesNo"
      role="group"
      aria-label={fr ? 'Confirmer l’ouverture du patient' : 'Confirm opening patient'}
    >
      <button
        type="button"
        className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
        disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
        onClick={onYes}
      >
        {fr ? 'Oui' : 'Yes'}
      </button>
      <button
        type="button"
        className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
        disabled={Boolean(isSending) || Boolean(yesNoDisabled)}
        onClick={onNo}
      >
        {fr ? 'Non' : 'No'}
      </button>
    </div>
  )
}

function noteTaskUiFrench(m: ChatMessage, language?: string): boolean {
  const lang = (language || '').toLowerCase()
  if (lang === 'french' || lang === 'fr') return true
  if (/noter|tâche/i.test(chatContentString(m.content))) return true
  return uiLangFr()
}

function NoteTaskPreviewCard(props: {
  preview: import('../types/chat').NoteTaskPreviewPayload
  actions?: { onEdit: () => void; onYes: () => void; onNo: () => void }
  isSending?: boolean
  yesNoDisabled?: boolean
  inline?: NoteTaskPreviewInlineEditProps
}) {
  const { preview, actions, isSending, yesNoDisabled, inline } = props
  const fr =
    (preview.language || '').toLowerCase() === 'french' ||
    (preview.language || '').toLowerCase() === 'fr' ||
    uiLangFr()
  const editLabel = fr ? 'Modifier' : 'Edit'
  const editing = Boolean(inline?.editing)
  const editVisible = Boolean(actions && !editing)
  const {
    inlineDraft = '',
    inlineSaving = false,
    inlineError,
    voiceTranscribe,
    onInlineDraftChange,
    onInlineSave,
    onInlineCancel,
  } = inline ?? {}

  return (
    <div
      className={[
        'vnw-secretaryMessagePreview',
        'vnw-sportsNocKvSummary',
        editing ? 'vnw-reminderPreviewCard--editing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={fr ? 'Aperçu de la tâche' : 'Task preview'}
    >
      <div className="vnw-sportsNocKvCard" role="list">
        {editing && onInlineDraftChange && onInlineSave && onInlineCancel ? (
          <NoteTaskContentInlineEditRow
            fr={fr}
            draft={inlineDraft}
            saving={inlineSaving}
            error={inlineError ?? null}
            languageHint={preview.language}
            voiceTranscribe={voiceTranscribe}
            onDraftChange={onInlineDraftChange}
            onSave={onInlineSave}
            onCancel={onInlineCancel}
          />
        ) : (
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Tâche' : 'Task'}</div>
            <div className="vnw-sportsNocKvVal">{preview.content?.trim() || '—'}</div>
            <div className="vnw-sportsNocKvAction">
              {editVisible ? (
                <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEdit}>
                  {editLabel}
                </button>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {actions ? (
        <div className="vnw-sportsNocYesNo vnw-reminderYesNo" role="group">
          <button
            type="button"
            className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
            disabled={Boolean(isSending) || Boolean(yesNoDisabled) || editing || inlineSaving}
            onClick={actions.onYes}
          >
            {fr ? 'Oui, continuer' : 'Yes, proceed'}
          </button>
          <button
            type="button"
            className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
            disabled={Boolean(isSending) || Boolean(yesNoDisabled) || editing || inlineSaving}
            onClick={actions.onNo}
          >
            {fr ? 'Non, annuler' : 'No, cancel'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Cancelled outcome — info graphic plus the task that was not saved. */
function NoteTaskCancelledCard({ m }: { m: ChatMessage }) {
  const fr = noteTaskUiFrench(m, m.notePreview?.language)
  const defaultTitle = fr ? 'Tâche annulée' : 'Task cancelled'
  const defaultSub = fr ? 'La tâche n’a pas été enregistrée.' : 'The task was not saved.'
  const content = (m.notePreview?.content || '').trim()
  return (
    <div
      className="vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={defaultTitle}
    >
      <PipelineInfoHero title={defaultTitle} subtitle={defaultSub} />
      {content ? (
        <div className="vnw-sportsNocKvCard vnw-reminderSuccessDetails" role="list">
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Tâche' : 'Task'}</div>
            <div className="vnw-sportsNocKvVal">{content}</div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

/** Success outcome — separate from the locked preview card above it. */
function NoteTaskSetSuccessCard({ m }: { m: ChatMessage }) {
  const text = chatContentString(m.content)
  const fr =
    /tâche\s+ajoutée/i.test(text) ||
    (m.notePreview?.language || '').toLowerCase() === 'french' ||
    uiLangFr()
  const defaultTitle = fr ? 'Tâche ajoutée avec succès' : 'Task added successfully'
  const defaultSub = fr
    ? 'Enregistrée dans votre liste de tâches.'
    : 'Saved to your task list.'
  const content = (m.notePreview?.content || '').trim()
  return (
    <div
      className="vnw-secretaryMessageSuccessShell vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={defaultTitle}
    >
      <PipelineSuccessHero title={defaultTitle} subtitle={defaultSub} />
      {content ? (
        <div className="vnw-sportsNocKvCard vnw-reminderSuccessDetails" role="list">
          <div className="vnw-sportsNocKvRow" role="listitem">
            <div className="vnw-sportsNocKvKey">{fr ? 'Tâche' : 'Task'}</div>
            <div className="vnw-sportsNocKvVal">{content}</div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function notePreviewIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist || m.notePreviewResolved || m.noteConfirmed || m.noteCancelled) return false
  return Boolean(m.notePreview && m.awaitingNoteConfirmation)
}

function isNoteOutcomeAssistantMessage(m: ChatMessage): boolean {
  return m.role === 'assistant' && Boolean(m.noteConfirmed || m.noteCancelled)
}

function ReminderPreviewCard(props: {
  preview: ReminderPreviewPayload
  actions?: {
    onEditPurpose: () => void
    onEditDay: () => void
    onEditTime: () => void
    onYes: () => void
    onNo: () => void
  }
  isSending?: boolean
  /** After another intent or a newer reminder preview; Yes/No only */
  yesNoDisabled?: boolean
  inline?: ReminderPreviewInlineEditProps
}) {
  const { preview, actions, isSending, yesNoDisabled, inline } = props
  const fr =
    (preview.language || '').toLowerCase() === 'french' ||
    (preview.language || '').toLowerCase() === 'fr' ||
    uiLangFr()
  const editLabel = fr ? 'Modifier' : 'Edit'
  const editing = Boolean(inline?.inlineEdit)
  return (
    <div
      className={[
        'vnw-secretaryMessagePreview',
        'vnw-sportsNocKvSummary',
        editing ? 'vnw-reminderPreviewCard--editing' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={fr ? 'Aperçu du rappel' : 'Reminder preview'}
    >
      <div className="vnw-sportsNocKvCard" role="list">
        <ReminderPreviewInlineRows
          preview={preview}
          fr={fr}
          editLabel={editLabel}
          editing={editing}
          actions={actions}
          showEditActions={Boolean(actions)}
          inline={inline ?? {}}
        />
      </div>
      {actions ? (
        <div
          className="vnw-sportsNocYesNo vnw-reminderYesNo"
          role="group"
          aria-label={
            yesNoDisabled
              ? fr
                ? 'Confirmation du rappel désactivée (conversation poursuivie ailleurs)'
                : 'Reminder confirmation disabled (conversation continued elsewhere)'
              : fr
                ? 'Confirmer le rappel'
                : 'Confirm reminder'
          }
        >
          <button
            type="button"
            className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
            disabled={Boolean(isSending) || Boolean(yesNoDisabled) || editing || Boolean(inline?.inlineSaving)}
            onClick={actions.onYes}
          >
            {fr ? 'Oui, continuer' : 'Yes, proceed'}
          </button>
          <button
            type="button"
            className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
            disabled={Boolean(isSending) || Boolean(yesNoDisabled) || editing || Boolean(inline?.inlineSaving)}
            onClick={actions.onNo}
          >
            {fr ? 'Non, annuler' : 'No, cancel'}
          </button>
        </div>
      ) : null}
    </div>
  )
}

/** Success outcome row — separate from the locked preview card above it. */
function ReminderSetSuccessCard({ m }: { m: ChatMessage }) {
  const text = chatContentString(m.content)
  const fr = /rappel\s+programm/i.test(text) || /\bobjet\s*:/i.test(text) || uiLangFr()
  const defaultTitle = fr ? 'Rappel programmé avec succès' : 'Reminder set successfully'
  const defaultSub = fr
    ? 'Ajouté à votre agenda — alerte à l’heure prévue.'
    : 'On your schedule — we’ll alert you when it’s time.'
  const titleLine =
    text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l && !/^(?:purpose|objet|day|jour|time|heure)\s*:/i.test(l)) || defaultTitle
  const preview = reminderSuccessPreviewFromMessage({
    content: m.content,
    reminderPreview: m.reminderPreview,
  })
  return (
    <div
      className="vnw-secretaryMessageSuccessShell vnw-secretaryMessagePreview vnw-sportsNocKvSummary vnw-reminderPreviewCard--confirmed"
      aria-label={defaultTitle}
    >
      <PipelineSuccessHero title={titleLine} subtitle={defaultSub} />
      {preview ? (
        <div className="vnw-sportsNocKvCard vnw-reminderSuccessDetails" role="list">
          <ReminderPreviewInlineRows
            preview={preview}
            fr={fr}
            editLabel=""
            editing={false}
            showEditActions={false}
            inline={{}}
          />
        </div>
      ) : null}
    </div>
  )
}

/** Success/cancel outcome row — not the editable preview card. */
function isReminderOutcomeAssistantMessage(m: ChatMessage): boolean {
  return m.role === 'assistant' && Boolean(m.reminderConfirmed || m.reminderCancelled)
}

function reminderPreviewIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist || m.reminderYesNoSuperseded || m.reminderPreviewResolved || m.reminderConfirmed || m.reminderCancelled) {
    return false
  }
  return Boolean(m.reminderPreview && m.awaitingRemindConfirmation)
}

/** Clarification steps include assistant text plus a partial preview; confirmation preview text lives on the card only. */
function reminderPreviewShowsClarificationText(m: ChatMessage): boolean {
  return Boolean(m.reminderPreview && !m.awaitingRemindConfirmation && chatContentTrim(m.content))
}

function secretaryPreviewIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist) return false
  return isSecretaryPreviewActionable(m)
}

function shouldShowSecretaryMessagePreview(m: ChatMessage): boolean {
  if (m.awaitingMessageEditField) return false
  return Boolean(m.secretaryMessage)
}

function secretarySendConfirmQuestion(fr: boolean): string {
  return fr ? 'Voulez-vous envoyer ce message ?' : 'Do you want to send this message?'
}

function secretarySendConfirmLabels(fr: boolean): { yes: string; no: string } {
  return fr
    ? { yes: 'Oui, envoyer', no: 'Ne pas envoyer' }
    : { yes: 'Yes, send it', no: 'No, do not send' }
}

function SecretaryMessagePreviewCard(props: {
  sm: SecretaryMessagePayload
  previewFr?: boolean
  actions?: {
    onEditPurpose: () => void
    onEditMessage: () => void
    onEditRecipientName: () => void
    onToggleUrgent: () => void
    urgentToggleBusy?: boolean
  }
  inline?: {
    field: SecretaryMessageInlineEditField | null
    draft: string
    saving: boolean
    error: string | null
    assigneeOptions?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
    onAssigneeOptionSelect?: (selectionId: string) => void
  }
  sendConfirm?: {
    onYes: () => void
    onNo: () => void
  }
  isSending?: boolean
}) {
  const { sm, actions, inline, sendConfirm, isSending, previewFr: previewFrProp } = props
  const fr = previewFrProp ?? secretaryPreviewFrFromMessage({ secretaryMessage: sm })
  const L = secretaryMessagePreviewLabels(fr)
  const editLabel = fr ? 'Modifier' : 'Edit'
  const whom = sm.whom
  const recipientName =
    typeof whom === 'object' && whom !== null
      ? (whom.name || '').trim() || (sm.secretary_name || '').trim()
      : (sm.secretary_name || '').trim()
  const recipientTypeRaw =
    typeof whom === 'object' && whom !== null ? (whom.type || '').trim() : typeof whom === 'string' ? whom : ''

  const purposeLine = (sm.subject || sm.purpose || sm.what || '').trim()
  const messageLine = (sm.message || sm.object || '').trim()
  const norm = (s: string) => s.replace(/\s+/g, ' ').replace(/[.!?]+$/g, '').trim().toLowerCase()
  const mergedPrimary = purposeLine || messageLine
  const primaryLabel = purposeLine ? L.purpose : L.message
  const showMessageRow = Boolean(
    messageLine && purposeLine && norm(messageLine) !== norm(purposeLine),
  )
  const err = (sm.discussion_initiate_error || '').trim()
  const sent = (sm.message_sent_status || '').trim().toLowerCase()
  const mentionedLine = mentionedContactPreviewValue(sm, fr)
  const requiresListConfirmation = ['multiple_assignees', 'assignee_pick_unresolved', 'assignee_pick_ambiguous_dob'].includes(
    String(sm.autofill_note || '').trim().toLowerCase(),
  )
  const awaitingSecretaryDirectoryPick =
    String(sm.autofill_note || '').trim().toLowerCase() === 'awaiting_secretary_directory_pick'
  const recipientPreview =
    requiresListConfirmation && !awaitingSecretaryDirectoryPick ? '—' : recipientName || '—'
  const editing = Boolean(inline?.field)
  const primaryEditField: SecretaryMessageInlineEditField = purposeLine ? 'purpose' : 'message'
  const showEditActions = Boolean(actions && !editing)
  const urgent = sm.urgent === true

  return (
    <div className="vnw-secretaryMessagePreview" aria-label="Message preview">
      {mergedPrimary ? (
        inline?.field === primaryEditField ? (
          <SecretaryMessageInlineEditRow
            field={primaryEditField}
            fr={fr}
            draft={inline.draft}
            saving={inline.saving}
            error={inline.error}
            assigneeOptions={inline.assigneeOptions}
            voiceTranscribe={inline.voiceTranscribe}
            onDraftChange={inline.onDraftChange}
            onSave={inline.onSave}
            onCancel={inline.onCancel}
            onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
          />
        ) : (
          <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--purpose">
            <span className="vnw-secretaryMessagePreviewLabel">{primaryLabel}</span>
            <span className="vnw-secretaryMessagePreviewValue vnw-secretaryMessagePreviewValue--emphasis">
              {mergedPrimary}
            </span>
            <span className="vnw-sportsNocKvAction">
              {showEditActions ? (
                <button
                  type="button"
                  className="vnw-sportsNocKvEdit"
                  onClick={primaryEditField === 'purpose' ? actions!.onEditPurpose : actions!.onEditMessage}
                >
                  {editLabel}
                </button>
              ) : null}
            </span>
          </div>
        )
      ) : null}
      {showMessageRow ? (
        inline?.field === 'message' ? (
          <SecretaryMessageInlineEditRow
            field="message"
            fr={fr}
            draft={inline.draft}
            saving={inline.saving}
            error={inline.error}
            assigneeOptions={inline.assigneeOptions}
            voiceTranscribe={inline.voiceTranscribe}
            onDraftChange={inline.onDraftChange}
            onSave={inline.onSave}
            onCancel={inline.onCancel}
            onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
          />
        ) : (
          <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--body">
            <span className="vnw-secretaryMessagePreviewLabel">{L.message}</span>
            <span className="vnw-secretaryMessagePreviewValue">{messageLine}</span>
            <span className="vnw-sportsNocKvAction">
              {showEditActions ? (
                <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEditMessage}>
                  {editLabel}
                </button>
              ) : null}
            </span>
          </div>
        )
      ) : null}
      {inline?.field === 'recipient_name' ? (
        <SecretaryMessageInlineEditRow
          field="recipient_name"
          fr={fr}
          draft={inline.draft}
          saving={inline.saving}
          error={inline.error}
          assigneeOptions={inline.assigneeOptions}
          voiceTranscribe={inline.voiceTranscribe}
          onDraftChange={inline.onDraftChange}
          onSave={inline.onSave}
          onCancel={inline.onCancel}
          onAssigneeOptionSelect={inline.onAssigneeOptionSelect}
        />
      ) : (
        <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--recipient">
          <span className="vnw-secretaryMessagePreviewLabel">{L.recipient}</span>
          <span className="vnw-secretaryMessagePreviewValue vnw-secretaryMessagePreviewValue--emphasis">
            {recipientPreview}
          </span>
          <span className="vnw-sportsNocKvAction">
            {showEditActions ? (
              <button type="button" className="vnw-sportsNocKvEdit" onClick={actions!.onEditRecipientName}>
                {editLabel}
              </button>
            ) : null}
          </span>
        </div>
      )}
      <div className="vnw-secretaryMessagePreviewRow">
        <span className="vnw-secretaryMessagePreviewLabel">{L.recipientType}</span>
        <span className="vnw-secretaryMessagePreviewValue">{recipientTypeLabel(recipientTypeRaw, fr)}</span>
        <span className="vnw-sportsNocKvAction" aria-hidden="true" />
      </div>
      <div className="vnw-secretaryMessagePreviewRow">
        <span className="vnw-secretaryMessagePreviewLabel">{L.urgent}</span>
        <span className="vnw-secretaryMessagePreviewValue vnw-secretaryMessagePreviewValue--emphasis vnw-secretaryMessagePreviewValue--toggle">
          <span>{urgent ? L.urgentOn : L.urgentOff}</span>
        </span>
        <span className="vnw-sportsNocKvAction">
          {showEditActions ? (
            <button
              type="button"
              className={`vnw-secretaryToggle${urgent ? ' vnw-secretaryToggle--on' : ''}`}
              role="switch"
              aria-checked={urgent}
              aria-label={urgent ? L.clearUrgent : L.markUrgent}
              disabled={actions!.urgentToggleBusy === true}
              onClick={actions!.onToggleUrgent}
            >
              <span className="vnw-secretaryToggleKnob" aria-hidden="true" />
            </button>
          ) : null}
        </span>
      </div>
      {mentionedLine ? (
        <div className="vnw-secretaryMessagePreviewRow vnw-secretaryMessagePreviewRow--mentioned">
          <span className="vnw-secretaryMessagePreviewLabel">{L.mentionedInMessage}</span>
          <span className="vnw-secretaryMessagePreviewValue">{mentionedLine}</span>
          <span className="vnw-sportsNocKvAction" aria-hidden="true" />
        </div>
      ) : null}
      {err ? (
        <div className="vnw-secretaryMessagePreviewFoot vnw-secretaryMessagePreviewFoot--error" role="alert">
          <span className="vnw-secretaryMessagePreviewFootLabel">{L.error}</span> {err}
        </div>
      ) : null}
      {sent === 'failure' && !err ? (
        <div className="vnw-secretaryMessagePreviewFoot vnw-secretaryMessagePreviewFoot--error" role="alert">
          {fr ? "L'envoi du message a échoué." : 'Message could not be sent.'}
        </div>
      ) : null}
      {sendConfirm ? (
        <>
          <p className="vnw-secretaryMessageSendQuestion">{secretarySendConfirmQuestion(fr)}</p>
          <div
            className="vnw-sportsNocYesNo vnw-secretaryMessageYesNo"
            role="group"
            aria-label={fr ? 'Confirmer l’envoi du message' : 'Confirm sending this message'}
          >
            <button
              type="button"
              className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--yes"
              disabled={Boolean(isSending) || editing || Boolean(inline?.saving)}
              onClick={sendConfirm.onYes}
            >
              {secretarySendConfirmLabels(fr).yes}
            </button>
            <button
              type="button"
              className="vnw-sportsNocYesNoBtn vnw-sportsNocYesNoBtn--no"
              disabled={Boolean(isSending) || editing || Boolean(inline?.saving)}
              onClick={sendConfirm.onNo}
            >
              {secretarySendConfirmLabels(fr).no}
            </button>
          </div>
        </>
      ) : null}
    </div>
  )
}

function safePrescriptionDocumentUrl(raw: string | null | undefined): string | undefined {
  if (typeof raw !== 'string') return undefined
  const u = raw.trim()
  if (!u.startsWith('https://')) return undefined
  try {
    const p = new URL(u)
    if (p.protocol !== 'https:') return undefined
    const h = p.hostname.toLowerCase()
    if (h.endsWith('.amazonaws.com') || isMedicalLinkHostname(h)) {
      return u
    }
    return undefined
  } catch {
    return undefined
  }
}

type MessageListProps = {
  messages: ChatMessage[]
  isSending?: boolean
  /** Voice turn: command bubble is visible while Nova intent runs. */
  voiceIntentProcessing?: boolean
  voiceProcessingUserMessageId?: string | null
  listRef: RefObject<HTMLDivElement | null>
  endRef: RefObject<HTMLDivElement | null>
  onScroll?: (e: UIEvent<HTMLDivElement>) => void
  onPatientOptionSelect?: (selectionId: string) => void
  onFolderOptionSelect?: (selectionId: string) => void
  onAssigneeOptionSelect?: (selectionId: string) => void
  onRecipientTypeOptionSelect?: (selectionId: string) => void
  onDeliveryChannelOptionSelect?: (selectionId: string) => void
  onPrescriptionTypeOptionSelect?: (selectionId: string) => void
  /** Tap a location card during sports NOC location step. */
  onSportsNocLocationSelect?: (locationId: number) => void
  onSportsNocSportToggle?: (messageId: string, fieldId: number) => void
  onSportsNocSportsConfirm?: (messageId: string) => void
  onSportsNocCommentsSubmit?: (text: string) => void
  /** Load the next slice of pending patient matches (same session as voice "show more"). */
  onPatientOptionsShowMore?: () => void
  /** MediKaLLink session for GET unread-messages (widget). */
  getMedicalLinkCredentials?: UnreadMessagesListProps['getCredentials']
  onUnreadRowsLoaded?: (messageId: string, rows: NormalizedUnreadMessage[]) => void
  onUnreadMessageOpen?: (messageId: string, row: NormalizedUnreadMessage, index: number) => void
  onUnreadReadAloudRequest?: (messageId: string, rows: NormalizedUnreadMessage[]) => void
  onUnreadReadAloudDecline?: (messageId: string) => void
  /** Sports no-objection certificate summary card (guided flow). */
  sportsNocActions?: {
    onEditLocation?: (summaryMessageId: string) => void
    onEditSports: (summaryMessageId: string) => void
    onEditComments: (summaryMessageId: string) => void
  }
  sportsNocSummaryInlineEdit?: SportsNocSummaryInlineEditProps | null
  /** Yes/No cards after sports NOC summary (generate / mail). */
  onSportsNocYesNo?: (messageId: string, kind: SportsNocYesNoKind, choice: 'yes' | 'no') => void
  /** Yes/No cards for renewed ordonnance mail send. */
  onRenewOrdonnanceMailYesNo?: (messageId: string, choice: 'yes' | 'no') => void
  /** Mail confirm unlocked after renewed document preview opens. */
  renewOrdonnanceMailConfirmUnlockedIds?: ReadonlySet<string>
  /** Success card unlocked after renewed document preview iframe loads. */
  renewOrdonnanceSuccessUnlockedIds?: ReadonlySet<string>
  /** Reminder preview action buttons. */
  onReminderPreviewAction?: (
    messageId: string,
    action: 'yes' | 'no' | 'edit_purpose' | 'edit_day' | 'edit_time',
  ) => void
  onNotePreviewAction?: (messageId: string, action: 'yes' | 'no' | 'edit') => void
  notePreviewInlineEdit?: {
    messageId: string | null
    draft: string
    saving: boolean
    error: string | null
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
  }
  reminderPreviewInlineEdit?: {
    messageId: string | null
    field: ReminderPreviewEditField | null
    draft: string
    saving: boolean
    error: string | null
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
  }
  secretaryPreviewInlineEdit?: {
    messageId: string | null
    field: SecretaryMessageInlineEditField | null
    draft: string
    saving: boolean
    error: string | null
    assigneeOptions?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
    showAssigneeOptions?: boolean
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
  }
  onSecretaryPreviewAction?: (
    messageId: string,
    action: 'edit_purpose' | 'edit_message' | 'edit_recipient_name' | 'edit_recipient_type' | 'toggle_urgent',
  ) => void
  secretaryUrgentToggleBusyMessageId?: string | null
  /** Yes/No send confirmation on secretary message preview (not chat footer). */
  onSecretarySendConfirm?: (messageId: string, choice: 'yes' | 'no') => void
  /** Yes/No send confirmation on mail document preview (not chat footer). */
  onMailSendConfirm?: (messageId: string, choice: 'yes' | 'no') => void
  /** Inline edit on mail document preview (subject, message, recipient). */
  mailDocumentPreviewInlineEdit?: {
    messageId: string | null
    field: 'subject' | 'message' | 'recipient_name' | null
    draft: string
    saving: boolean
    error: string | null
    assigneeOptions?: Array<{ selection_id: string; label: string; dob: string; gender?: string }>
    voiceTranscribe?: ReminderInlineVoiceTranscribeConfig
    onDraftChange: (value: string) => void
    onSave: () => void
    onCancel: () => void
    onAssigneeOptionSelect?: (selectionId: string) => void
  }
  onMailPreviewEdit?: (
    messageId: string,
    action: 'edit_subject' | 'edit_message' | 'edit_recipient_name',
  ) => void
  /** Yes/No when a document is requested for a patient other than the active chart. */
  onPatientSwitchConfirm?: (messageId: string, choice: 'yes' | 'no') => void
  /** Yes/No on ReadPatientModality read-aloud confirmation (in-chat, not composer bar). */
  onReadModalityYesNo?: (messageId: string, choice: 'yes' | 'no') => void
  /** Yes/No on check-exam-receipt open confirmation (in-chat preview card). */
  onCheckExamReceiptYesNo?: (messageId: string, choice: 'yes' | 'no') => void
}

export function MessageList(props: MessageListProps) {
  const {
    messages,
    isSending,
    voiceIntentProcessing,
    voiceProcessingUserMessageId,
    listRef,
    endRef,
    onScroll,
    onPatientOptionSelect,
    onFolderOptionSelect,
    onAssigneeOptionSelect,
    onRecipientTypeOptionSelect,
    onDeliveryChannelOptionSelect,
    onPrescriptionTypeOptionSelect,
    onSportsNocLocationSelect,
    onSportsNocSportToggle,
    onSportsNocSportsConfirm,
    onSportsNocCommentsSubmit,
    onPatientOptionsShowMore,
    getMedicalLinkCredentials,
    onUnreadRowsLoaded,
    onUnreadMessageOpen,
    onUnreadReadAloudRequest,
    onUnreadReadAloudDecline,
    sportsNocActions,
    sportsNocSummaryInlineEdit,
    onSportsNocYesNo,
    onRenewOrdonnanceMailYesNo,
    renewOrdonnanceMailConfirmUnlockedIds,
    renewOrdonnanceSuccessUnlockedIds,
    onReminderPreviewAction,
    onNotePreviewAction,
    notePreviewInlineEdit,
    reminderPreviewInlineEdit,
    secretaryPreviewInlineEdit,
    onSecretaryPreviewAction,
    secretaryUrgentToggleBusyMessageId,
    onSecretarySendConfirm,
    onMailSendConfirm,
    mailDocumentPreviewInlineEdit,
    onMailPreviewEdit,
    onPatientSwitchConfirm,
    onReadModalityYesNo,
    onCheckExamReceiptYesNo,
  } = props

  useWheelScrollIsolation(listRef)

  const footerTimingLabels = {
    voiceToChat: getChatUiStrings().footerVoiceToChat,
    intent: getChatUiStrings().footerIntent,
    normalize: getChatUiStrings().footerNormalize,
    typedCommand: getChatUiStrings().footerTypedCommand,
  }
  const gapSincePreviousLabel = getChatUiStrings().footerGapSincePrevious

  function interMessageGap(messageIndex: number): ReactNode {
    if (messageIndex <= 0) return null
    const prev = messages[messageIndex - 1]
    const curr = messages[messageIndex]
    if (!prev || !curr) return null
    const gapLabel = formatInterMessageGapSeconds(curr.timestamp - prev.timestamp)
    if (!gapLabel) return null
    return (
      <div
        className="vnw-messageGap"
        aria-label={`${gapSincePreviousLabel} ${gapLabel}`}
        title={`${gapSincePreviousLabel} ${gapLabel}`}
      >
        {gapLabel}
      </div>
    )
  }

  function messageFooterTime(m: ChatMessage): string {
    if (m.role === 'user') {
      return formatUserFooterTimeWithTranscribe(
        m.timestamp,
        m.transcribeProcessingMs,
        m.typedNormalizeProcessingMs,
        footerTimingLabels,
      )
    }
    if (m.typedCommandProcessingMs != null) {
      return formatTypedCommandFooterTime(
        m.timestamp,
        m.typedCommandProcessingMs,
        footerTimingLabels,
      )
    }
    const latencyMs = m.intentProcessingMs ?? m.typedCommandProcessingMs
    return formatAssistantFooterTimeWithLatency(m.timestamp, latencyMs, footerTimingLabels)
  }

  function hasUserMessageAfter(assistantIndex: number): boolean {
    for (let j = assistantIndex + 1; j < messages.length; j++) {
      if (messages[j]?.role === 'user') return true
    }
    return false
  }

  function renderTypingDots(className = 'vnw-messageBubble'): ReactNode {
    return (
      <div className={className} role="status" aria-live="polite">
        <span className="vnw-typingDot">•</span>
        <span className="vnw-typingDot">•</span>
        <span className="vnw-typingDot">•</span>
      </div>
    )
  }

  return (
    <div
      ref={listRef}
      className="vnw-messageList"
      role="log"
      aria-live="polite"
      onScroll={onScroll}
    >
      {messages.map((m, messageIndex) => {
        const hist = isHistorySnapshot(m)
        const messageBody =
        m.role === 'user' ? (
          <div
            key={m.id}
            className={
              voiceIntentProcessing && voiceProcessingUserMessageId === m.id
                ? 'vnw-message vnw-message--user vnw-message--awaitingResponse'
                : 'vnw-message vnw-message--user'
            }
          >
            <div className="vnw-messageColumn vnw-messageColumn--user">
              <div className="vnw-messageBubble">{chatContentString(m.content)}</div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
            </div>
          </div>
        ) : m.sportsNocGenerating ? (
          <div
            key={m.id}
            className="vnw-message vnw-message--assistant vnw-message--sportsNocGenerating"
          >
            <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-sportsNocGenerating" role="status" aria-live="polite">
                {chatContentString(m.content)}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
            </div>
          </div>
        ) : m.renewOrdonnanceRenewing ? (
          <div
            key={m.id}
            className="vnw-message vnw-message--assistant vnw-message--sportsNocGenerating"
          >
            <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-sportsNocGenerating" role="status" aria-live="polite">
                {chatContentString(m.content)}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
            </div>
          </div>
        ) : m.renewOrdonnanceRenewalCompleted ? (
          renewOrdonnanceSuccessUiReady(
            m,
            renewOrdonnanceSuccessUnlockedIds ?? new Set(),
            hist,
          ) ? (
            <div
              key={m.id}
              className="vnw-message vnw-message--assistant vnw-message--sportsNocRow"
            >
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
                <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                  <div className="vnw-sportsNocCardSlot">
                    <RenewOrdonnanceSuccessCard fr={renewOrdonnanceUiFr(m)} />
                  </div>
                </div>
                <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                  {messageFooterTime(m)}
                </time>
              </div>
            </div>
          ) : null
        ) : m.renewOrdonnanceMailResolved ? (
          null
        ) : isRenewOrdonnanceMailConfirmRow(m) &&
          !renewOrdonnanceMailConfirmUiReady(
            m,
            renewOrdonnanceMailConfirmUnlockedIds ?? new Set(),
          ) ? (
          null
        ) : isRenewOrdonnanceMailConfirmRow(m) ? (
          <div
            key={m.id}
            className="vnw-message vnw-message--assistant vnw-message--sportsNocRow"
          >
            <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                {chatContentTrim(m.content) ? (
                  <div className="vnw-messageBubble">
                    <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                  </div>
                ) : null}
                {m.mailDocument ? (
                  <div className="vnw-sportsNocCardSlot">
                    <MailDocumentPreviewCard
                      mail={m.mailDocument}
                      fr={renewOrdonnanceUiFr(m)}
                      isSending={isSending}
                      {...mailDocumentPreviewCardExtras(m, hist, mailDocumentPreviewInlineEdit, onMailPreviewEdit)}
                      sendConfirm={
                        !hist &&
                        !m.renewOrdonnanceMailResolved &&
                        onRenewOrdonnanceMailYesNo &&
                        renewOrdonnanceMailConfirmUiReady(
                          m,
                          renewOrdonnanceMailConfirmUnlockedIds ?? new Set(),
                        )
                          ? {
                              onYes: () => onRenewOrdonnanceMailYesNo(m.id, 'yes'),
                              onNo: () => onRenewOrdonnanceMailYesNo(m.id, 'no'),
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : null}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
            </div>
          </div>
        ) : (
          <div
            key={m.id}
            className={
              isSportsNocGenerateConfirmRow(m) ||
              m.sportsNocCertificateGenerated ||
              isSportsNocMailConfirmRow(m) ||
              m.sportsNocSummary ||
              (m.sportsNocYesNoPrompt && chatContentTrim(m.content))
                ? 'vnw-message vnw-message--assistant vnw-message--sportsNocRow'
                : m.pendingModalityExam?.status === 'pending'
                  ? 'vnw-message vnw-message--assistant vnw-message--pendingModalityExam'
                  : 'vnw-message vnw-message--assistant'
            }
          >
            {m.sportsNocCertificateGenerated ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
                <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                  <div className="vnw-sportsNocCardSlot">
                    <SportsCertificateGeneratedSuccessCard fr={sportsNocUiFr(m)} />
                  </div>
                </div>
                <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                  {messageFooterTime(m)}
                </time>
              </div>
            ) : isSportsNocMailConfirmRow(m) ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
                <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                  {chatContentTrim(m.content) ? (
                    <div className="vnw-messageBubble">
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    </div>
                  ) : null}
                  {m.mailDocument ? (
                    <div className="vnw-sportsNocCardSlot">
                      <MailDocumentPreviewCard
                        mail={m.mailDocument}
                        fr={sportsNocUiFr(m)}
                        isSending={isSending}
                        {...mailDocumentPreviewCardExtras(m, hist, mailDocumentPreviewInlineEdit, onMailPreviewEdit)}
                        actionCardsConfirm={
                          !hist && !m.sportsNocYesNoResolved && onSportsNocYesNo
                            ? (() => {
                                const mailLabels = sportsNocMailConfirmLabels(sportsNocUiFr(m))
                                return {
                                  onYes: () => onSportsNocYesNo(m.id, 'mail_certificate', 'yes'),
                                  onNo: () => onSportsNocYesNo(m.id, 'mail_certificate', 'no'),
                                  yesLabel: mailLabels.yes,
                                  noLabel: mailLabels.no,
                                  ariaLabel: mailLabels.ariaLabel,
                                }
                              })()
                            : undefined
                        }
                      />
                    </div>
                  ) : null}
                </div>
                <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                  {messageFooterTime(m)}
                </time>
              </div>
            ) : isSportsNocGenerateConfirmRow(m) && m.sportsNocSummary ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                {(() => {
                  const editingThisRow = sportsNocSummaryInlineEdit?.messageId === m.id
                  return (
                    <>
                {chatContentTrim(m.content) ? (
                  <div className="vnw-messageBubble">
                    <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                  </div>
                ) : null}
                <div className="vnw-sportsNocCardSlot">
                  <SportsNocSummaryCard
                    summary={m.sportsNocSummary}
                    fr={sportsNocUiFr(m)}
                    generatePreview
                    actions={
                      !hist && sportsNocActions && !editingThisRow
                        ? {
                            onEditLocation: sportsNocActions.onEditLocation
                              ? () => sportsNocActions.onEditLocation!(m.id)
                              : undefined,
                            onEditSports: () => sportsNocActions.onEditSports(m.id),
                            onEditComments: () => sportsNocActions.onEditComments(m.id),
                          }
                        : undefined
                    }
                    inlineEdit={
                      sportsNocSummaryInlineEdit?.messageId === m.id
                        ? sportsNocSummaryInlineEdit
                        : null
                    }
                  />
                </div>
                {!hist &&
                m.sportsNocYesNoPrompt?.kind === 'generate_certificate' &&
                !m.sportsNocYesNoResolved &&
                !editingThisRow &&
                onSportsNocYesNo ? (
                  <SportsNocGenerateConfirmCards
                    fr={sportsNocUiFr(m)}
                    isSending={isSending}
                    onYes={() => onSportsNocYesNo(m.id, 'generate_certificate', 'yes')}
                    onNo={() => onSportsNocYesNo(m.id, 'generate_certificate', 'no')}
                  />
                ) : null}
                    </>
                  )
                })()}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
              </div>
            ) : m.sportsNocSummary || (m.sportsNocYesNoPrompt && chatContentTrim(m.content)) ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                {chatContentTrim(m.content) ? (
                  <div className="vnw-messageBubble">
                    <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                  </div>
                ) : null}
                {m.sportsNocSummary ? (
                  <div className="vnw-sportsNocCardSlot">
                    <SportsNocSummaryCard
                      summary={m.sportsNocSummary}
                      fr={sportsNocUiFr(m)}
                      actions={
                        !hist && sportsNocActions
                          ? {
                              onEditLocation: sportsNocActions.onEditLocation
                                ? () => sportsNocActions.onEditLocation!(m.id)
                                : undefined,
                              onEditSports: () => sportsNocActions.onEditSports(m.id),
                              onEditComments: () => sportsNocActions.onEditComments(m.id),
                            }
                          : undefined
                      }
                    />
                  </div>
                ) : null}
                {!hist &&
                m.sportsNocYesNoPrompt?.kind === 'generate_certificate' &&
                !m.sportsNocYesNoResolved &&
                onSportsNocYesNo ? (
                  <SportsNocGenerateConfirmCards
                    fr={sportsNocUiFr(m)}
                    isSending={isSending}
                    onYes={() => onSportsNocYesNo(m.id, 'generate_certificate', 'yes')}
                    onNo={() => onSportsNocYesNo(m.id, 'generate_certificate', 'no')}
                  />
                ) : null}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
              </div>
            ) : isSportsNocSilentTrigger(m) ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant" aria-hidden="true" />
            ) : isSportsNocOptionPickerMessage(m) ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
                <div className="vnw-messageStack vnw-messageStack--sportsNoc">
                  {m.sportsNocEditUiOnly ? (
                    chatContentTrim(m.content) ? (
                      <div className="vnw-sportsNocEditUiLabel">{chatContentString(m.content)}</div>
                    ) : null
                  ) : chatContentTrim(m.content) ? (
                    <div className="vnw-messageBubble vnw-messageBubble--sportsNocPrompt">
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    </div>
                  ) : null}
                  {m.uiType === 'sports_noc_location_options' &&
                  m.sportsNocLocationOptions &&
                  m.sportsNocLocationOptions.length > 0 ? (
                    m.sportsNocLocationPickerStyle === 'dropdown' ? (
                      <SportsNocLocationDropdown
                        options={m.sportsNocLocationOptions}
                        currentLocationId={m.sportsNocCurrentLocationId}
                        fr={sportsNocUiFr(m)}
                        isSending={isSending}
                        onSelect={onSportsNocLocationSelect}
                      />
                    ) : (
                      <SportsNocLocationOptionCards
                        options={m.sportsNocLocationOptions}
                        fr={sportsNocUiFr(m)}
                        resolved={m.sportsNocLocationOptionsResolved}
                        hist={hist}
                        isSending={isSending}
                        onSelect={onSportsNocLocationSelect}
                      />
                    )
                  ) : null}
                  {m.uiType === 'sports_noc_sports_options' &&
                  m.sportsNocSportOptions &&
                  m.sportsNocSportOptions.length > 0 ? (
                    <SportsNocSportsOptionCards
                      messageId={m.id}
                      options={m.sportsNocSportOptions}
                      selectedIds={m.sportsNocSportSelectedIds ?? []}
                      fr={sportsNocUiFr(m)}
                      resolved={m.sportsNocSportOptionsResolved}
                      hist={hist}
                      isSending={isSending}
                      onToggle={onSportsNocSportToggle}
                      onConfirm={onSportsNocSportsConfirm}
                    />
                  ) : null}
                  {m.uiType === 'sports_noc_comments_input' ? (
                    <SportsNocCommentsInput
                      initialDraft={m.sportsNocCommentsDraft}
                      fr={sportsNocUiFr(m)}
                      isSending={isSending}
                      onSubmit={onSportsNocCommentsSubmit}
                    />
                  ) : null}
                  {m.sportsNocNeedsClarification === true &&
                  m.sportsNocAwaitingField === 'comments' &&
                  // Avoid double rendering when the real comments input arrives.
                  m.uiType !== 'sports_noc_comments_input' ? (
                    <div className="vnw-sportsNocCommentsPromptActions">
                      <button
                        type="button"
                        className="vnw-sportsNocCommentsNoBtn"
                        disabled={Boolean(isSending)}
                        onClick={() => onSportsNocCommentsSubmit?.(sportsNocUiFr(m) ? 'non' : 'no')}
                      >
                        {sportsNocUiFr(m) ? 'Aucun commentaire' : 'No comments'}
                      </button>
                    </div>
                  ) : null}
                </div>
                <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                  {messageFooterTime(m)}
                </time>
              </div>
            ) : m.awaitingMessageEditField ? (
              <div className="vnw-messageColumn vnw-messageColumn--assistant" aria-hidden="true" />
            ) : (
              <div className="vnw-messageColumn vnw-messageColumn--assistant">
              <div className="vnw-messageStack">
                <div className="vnw-messageBubble">
                  {!hist && m.omitFromServerChatHistory ? (
                    <div className="vnw-sessionPendingDebug" role="note">
                      <div className="vnw-sessionPendingDebugText">{chatContentString(m.content)}</div>
                    </div>
                  ) : !hist && m.getLastMessageUi && getMedicalLinkCredentials ? (
                  <div className="vnw-unreadBlock" aria-label="Patient last message">
                    {chatContentTrim(m.content) ? (
                      <div className="vnw-messageBodyText vnw-unreadBlockTitle">{m.content}</div>
                    ) : null}
                    <GetLastMessageList
                      request={m.getLastMessageUi}
                      getCredentials={getMedicalLinkCredentials}
                      parentMessageId={m.id}
                      onRowsLoaded={onUnreadRowsLoaded}
                      onMessageOpen={onUnreadMessageOpen}
                      hideReadAloudPrompt={
                        m.unreadMessagesReadAloudDismissed === true ||
                        m.unreadMessagesReadAloudPlayed === true
                      }
                      onReadAloudRequest={onUnreadReadAloudRequest}
                      onReadAloudDecline={onUnreadReadAloudDecline}
                    />
                  </div>
                ) : m.getLastMessageUi ? (
                  <div className="vnw-unreadBlock vnw-unreadBlock--fallback" role="note">
                    <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    <p className="vnw-unreadBlockFallback">
                      {hist
                        ? uiLangFr()
                          ? 'Dernier message patient (aperçu historique — non interactif).'
                          : 'Patient last message (history snapshot — not interactive).'
                        : 'Connect MediKaLLink credentials in the host session to load the message here.'}
                    </p>
                  </div>
                  ) : !hist && m.unreadMessagesUi && getMedicalLinkCredentials ? (
                  <div className="vnw-unreadBlock" aria-label="Unread messages list">
                    {chatContentTrim(m.content) ? (
                      <div className="vnw-messageBodyText vnw-unreadBlockTitle">{m.content}</div>
                    ) : null}
                    <UnreadMessagesList
                      variant={m.unreadMessagesUi.variant}
                      language={m.unreadMessagesUi.language ?? 'en'}
                      getCredentials={getMedicalLinkCredentials}
                      parentMessageId={m.id}
                      onRowsLoaded={onUnreadRowsLoaded}
                      onMessageOpen={onUnreadMessageOpen}
                      hideReadAloudPrompt={
                        m.unreadMessagesReadAloudDismissed === true ||
                        m.unreadMessagesReadAloudPlayed === true
                      }
                      onReadAloudRequest={onUnreadReadAloudRequest}
                      onReadAloudDecline={onUnreadReadAloudDecline}
                    />
                  </div>
                ) : m.unreadMessagesUi ? (
                  <div className="vnw-unreadBlock vnw-unreadBlock--fallback" role="note">
                    <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    <p className="vnw-unreadBlockFallback">
                      {hist
                        ? uiLangFr()
                          ? 'Liste des messages non lus (aperçu historique — non interactive).'
                          : 'Unread messages list (history snapshot — not interactive).'
                        : 'Connect MediKaLLink credentials in the host session to load unread messages here.'}
                    </p>
                  </div>
                ) : m.navigationMeta ? (
                  <div className="vnw-messageBodyText">{navigationChatBubbleText(m)}</div>
                ) : renewOrdonnanceAssistantBubbleText(m) ? (
                  <div className="vnw-messageBodyText" aria-label="Renew ordonnance">
                    {renewOrdonnanceAssistantBubbleText(m)}
                  </div>
                ) : m.bloodTestPrescriptionMeta ? (
                  <div className="vnw-messageBodyText" aria-label="Blood test prescription">
                    {(m.uiType === 'prescription_type_options'
                      ? prescriptionTypeOptionsPromptText(m)
                      : chatContentTrim(m.content)) ||
                      (m.bloodTestPrescriptionMeta.needs_clarification
                        ? m.bloodTestPrescriptionMeta.clarification_prompt || ''
                        : '')}
                  </div>
                ) : m.prescriptionMeta ? (
                  <div className="vnw-messageBodyText" aria-label="Prescription">
                    {(m.uiType === 'prescription_type_options'
                      ? prescriptionTypeOptionsPromptText(m)
                      : chatContentTrim(m.content)) ||
                      (m.prescriptionMeta.needs_clarification
                        ? m.prescriptionMeta.clarification_prompt || ''
                        : m.prescriptionMeta.intent_action === 'view_prescription'
                          ? uiLangFr()
                            ? `Ouverture de ${prescriptionFriendlyLabel(m.prescriptionMeta.prescription_type, true)}${
                                m.prescriptionMeta.resolved_patient_name?.trim()
                                  ? ` pour ${m.prescriptionMeta.resolved_patient_name.trim()}`
                                  : ''
                              }.`
                            : `Opening ${prescriptionFriendlyLabel(m.prescriptionMeta.prescription_type, false)}${
                                m.prescriptionMeta.resolved_patient_name?.trim()
                                  ? ` for ${m.prescriptionMeta.resolved_patient_name.trim()}`
                                  : ''
                              }.`
                          : '')}
                  </div>
                ) : m.mailDocument &&
                  !(
                    isRenewOrdonnanceMailConfirmRow(m) &&
                    !renewOrdonnanceMailConfirmUiReady(
                      m,
                      renewOrdonnanceMailConfirmUnlockedIds ?? new Set(),
                    )
                  ) ? (
                  <>
                    {mailDocumentSentSuccess(m.mailDocument) ? (
                      <MailDocumentSentSuccessCard mail={m.mailDocument} fr={mailMessageFrench(m)} />
                    ) : (
                      <>
                        {m.mailDocument.awaitingMailConfirmation ? (
                          <MailDocumentPreviewCard
                            mail={m.mailDocument}
                            fr={mailMessageFrench(m)}
                            isSending={isSending}
                            {...mailDocumentPreviewCardExtras(m, hist, mailDocumentPreviewInlineEdit, onMailPreviewEdit)}
                            sendConfirm={
                              mailPreviewIsActionable(m, hist) &&
                              onMailSendConfirm &&
                              !hasUserMessageAfter(messageIndex)
                                ? {
                                    onYes: () => onMailSendConfirm(m.id, 'yes'),
                                    onNo: () => onMailSendConfirm(m.id, 'no'),
                                  }
                                : undefined
                            }
                          />
                        ) : (
                          <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                        )}
                      </>
                    )}
                  </>
                ) : m.secretaryMessage ? (
                  <>
                    {String(m.secretaryMessage.message_sent_status || '').toLowerCase() === 'success' ? (
                      <SecretaryMessageSentSuccessCard sm={m.secretaryMessage} fr={secretaryPreviewFr(m)} />
                    ) : shouldShowCancellationNotice(m) ? (
                      <CancellationNotice m={m} />
                    ) : (
                      <>
                        {shouldShowSecretaryMessagePreview(m) ? (
                          <SecretaryMessagePreviewCard
                            sm={m.secretaryMessage}
                            previewFr={secretaryPreviewFr(m)}
                            isSending={isSending}
                            inline={
                              secretaryPreviewInlineEdit?.messageId === m.id
                                ? {
                                    field: secretaryPreviewInlineEdit.field,
                                    draft: secretaryPreviewInlineEdit.draft,
                                    saving: secretaryPreviewInlineEdit.saving,
                                    error: secretaryPreviewInlineEdit.error,
                                    assigneeOptions:
                                      secretaryPreviewInlineEdit.field === 'recipient_name' &&
                                      secretaryPreviewInlineEdit.showAssigneeOptions
                                        ? secretaryPreviewInlineEdit.assigneeOptions
                                        : undefined,
                                    voiceTranscribe: secretaryPreviewInlineEdit.voiceTranscribe,
                                    onDraftChange: secretaryPreviewInlineEdit.onDraftChange,
                                    onSave: secretaryPreviewInlineEdit.onSave,
                                    onCancel: secretaryPreviewInlineEdit.onCancel,
                                    onAssigneeOptionSelect,
                                  }
                                : undefined
                            }
                            actions={
                              secretaryPreviewIsActionable(m, hist) && onSecretaryPreviewAction
                                ? {
                                    onEditPurpose: () => onSecretaryPreviewAction(m.id, 'edit_purpose'),
                                    onEditMessage: () => onSecretaryPreviewAction(m.id, 'edit_message'),
                                    onEditRecipientName: () =>
                                      onSecretaryPreviewAction(m.id, 'edit_recipient_name'),
                                    onToggleUrgent: () => onSecretaryPreviewAction(m.id, 'toggle_urgent'),
                                    urgentToggleBusy: secretaryUrgentToggleBusyMessageId === m.id,
                                  }
                                : undefined
                            }
                            sendConfirm={
                              secretaryPreviewIsActionable(m, hist) &&
                              onSecretarySendConfirm &&
                              !hasUserMessageAfter(messageIndex)
                                ? {
                                    onYes: () => onSecretarySendConfirm(m.id, 'yes'),
                                    onNo: () => onSecretarySendConfirm(m.id, 'no'),
                                  }
                                : undefined
                            }
                          />
                        ) : null}
                        {chatContentTrim(m.content) && !m.awaitingMessageConfirmation ? (
                          <div className="vnw-messageBodyText">
                            {m.uiType === 'assignee_options'
                              ? assigneeOptionsPromptText(m)
                              : chatContentString(m.content)}
                          </div>
                        ) : null}
                      </>
                    )}
                  </>
                ) : isCheckExamReceiptConfirmationMessage(m) ? (
                  <>
                    {chatContentTrim(m.content) ? (
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    ) : null}
                    {onCheckExamReceiptYesNo &&
                    checkExamReceiptConfirmIsActionable(m, hist) &&
                    !m.checkExamReceiptYesNoResolved ? (
                      <CheckExamReceiptConfirmCard
                        m={m}
                        fr={checkExamReceiptUiFrench(m)}
                        isSending={isSending}
                        yesNoDisabled={Boolean(m.checkExamReceiptYesNoResolved)}
                        onYes={() => onCheckExamReceiptYesNo(m.id, 'yes')}
                        onNo={() => onCheckExamReceiptYesNo(m.id, 'no')}
                      />
                    ) : null}
                  </>
                ) : isReadModalityConfirmationMessage(m) ? (
                  <>
                    {chatContentTrim(m.content) ? (
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    ) : null}
                    {onReadModalityYesNo &&
                    readModalityConfirmIsActionable(m, hist) &&
                    !m.readModalityYesNoResolved ? (
                      <ReadModalityYesNo
                        fr={readModalityUiFrench(m)}
                        isSending={isSending}
                        yesNoDisabled={Boolean(m.readModalityYesNoResolved)}
                        onYes={() => onReadModalityYesNo(m.id, 'yes')}
                        onNo={() => onReadModalityYesNo(m.id, 'no')}
                      />
                    ) : null}
                  </>
                ) : isPatientSwitchConfirmationMessage(m) ? (
                  <>
                    {chatContentTrim(m.content) ? <div className="vnw-messageBodyText">{m.content}</div> : null}
                    {onPatientSwitchConfirm &&
                    patientSwitchConfirmIsActionable(m, hist) &&
                    !m.patientSwitchConfirmResolved ? (
                      <PatientSwitchYesNo
                        isSending={isSending}
                        yesNoDisabled={Boolean(m.patientSwitchConfirmResolved)}
                        onYes={() => onPatientSwitchConfirm(m.id, 'yes')}
                        onNo={() => onPatientSwitchConfirm(m.id, 'no')}
                      />
                    ) : null}
                  </>
                ) : shouldShowCancellationNotice(m) ? (
                  <CancellationNotice m={m} />
                ) : isNoteOutcomeAssistantMessage(m) && m.noteConfirmed ? (
                  <NoteTaskSetSuccessCard m={m} />
                ) : isNoteOutcomeAssistantMessage(m) && m.noteCancelled ? (
                  <NoteTaskCancelledCard m={m} />
                ) : isNoteOutcomeAssistantMessage(m) ? (
                  <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                ) : isReminderOutcomeAssistantMessage(m) && m.reminderConfirmed ? (
                  <ReminderSetSuccessCard m={m} />
                ) : isReminderOutcomeAssistantMessage(m) ? (
                  <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                ) : m.notePreview ? (
                  <>
                    {!m.awaitingNoteConfirmation && chatContentTrim(m.content) ? (
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    ) : null}
                    <NoteTaskPreviewCard
                      preview={m.notePreview}
                      isSending={isSending}
                      inline={
                        notePreviewInlineEdit?.messageId === m.id
                          ? {
                              editing: true,
                              inlineDraft: notePreviewInlineEdit.draft,
                              inlineSaving: notePreviewInlineEdit.saving,
                              inlineError: notePreviewInlineEdit.error,
                              voiceTranscribe: notePreviewInlineEdit.voiceTranscribe,
                              onInlineDraftChange: notePreviewInlineEdit.onDraftChange,
                              onInlineSave: notePreviewInlineEdit.onSave,
                              onInlineCancel: notePreviewInlineEdit.onCancel,
                            }
                          : undefined
                      }
                      actions={
                        notePreviewIsActionable(m, hist) && onNotePreviewAction
                          ? {
                              onEdit: () => onNotePreviewAction(m.id, 'edit'),
                              onYes: () => onNotePreviewAction(m.id, 'yes'),
                              onNo: () => onNotePreviewAction(m.id, 'no'),
                            }
                          : undefined
                      }
                    />
                  </>
                ) : m.reminderPreview ? (
                  <>
                    {reminderPreviewShowsClarificationText(m) ? (
                      <div className="vnw-messageBodyText">{chatContentString(m.content)}</div>
                    ) : null}
                    <ReminderPreviewCard
                      preview={m.reminderPreview}
                      isSending={isSending}
                      yesNoDisabled={Boolean(m.reminderYesNoSuperseded)}
                      inline={
                        reminderPreviewInlineEdit?.messageId === m.id
                          ? {
                              inlineEdit: reminderPreviewInlineEdit.field,
                              inlineDraft: reminderPreviewInlineEdit.draft,
                              inlineSaving: reminderPreviewInlineEdit.saving,
                              inlineError: reminderPreviewInlineEdit.error,
                              voiceTranscribe: reminderPreviewInlineEdit.voiceTranscribe,
                              onInlineDraftChange: reminderPreviewInlineEdit.onDraftChange,
                              onInlineSave: reminderPreviewInlineEdit.onSave,
                              onInlineCancel: reminderPreviewInlineEdit.onCancel,
                            }
                          : undefined
                      }
                      actions={
                        reminderPreviewIsActionable(m, hist) && onReminderPreviewAction
                          ? {
                              onEditPurpose: () => onReminderPreviewAction(m.id, 'edit_purpose'),
                              onEditDay: () => onReminderPreviewAction(m.id, 'edit_day'),
                              onEditTime: () => onReminderPreviewAction(m.id, 'edit_time'),
                              onYes: () => onReminderPreviewAction(m.id, 'yes'),
                              onNo: () => onReminderPreviewAction(m.id, 'no'),
                            }
                          : undefined
                      }
                    />
                  </>
                ) : (
                  m.content
                )}
              {!hist && !m.navigationMeta && intentUrlToOpen(m) && !m.openPatientDossierUrl && (
                <div className="vnw-intentUrlDebug" aria-label="Intent navigation URL">
                  <div className="vnw-prescriptionStructuredLine">
                    <span className="vnw-prescriptionStructuredLabel">URL to open</span>
                    <span className="vnw-prescriptionStructuredValue vnw-intentUrlDebugValue">
                      {(() => {
                        const u = intentUrlToOpen(m)?.trim()
                        return u ? (
                          <a
                            className="vnw-intentUrlDebugLink"
                            href={u}
                            target={m.openPatientDossierUrl || m.openNavigationUrl ? '_self' : '_blank'}
                            rel="noopener noreferrer"
                          >
                            {u}
                          </a>
                        ) : (
                          '—'
                        )
                      })()}
                    </span>
                  </div>
                </div>
              )}
              </div>
              {m.uiType === 'sports_noc_location_options' &&
              m.sportsNocLocationOptions &&
              m.sportsNocLocationOptions.length > 0 ? (
                <SportsNocLocationOptionCards
                  options={m.sportsNocLocationOptions}
                  fr={sportsNocUiFr(m)}
                  resolved={m.sportsNocLocationOptionsResolved}
                  hist={hist}
                  isSending={isSending}
                  onSelect={onSportsNocLocationSelect}
                />
              ) : null}
              {m.uiType === 'sports_noc_sports_options' &&
              m.sportsNocSportOptions &&
              m.sportsNocSportOptions.length > 0 ? (
                <SportsNocSportsOptionCards
                  messageId={m.id}
                  options={m.sportsNocSportOptions}
                  selectedIds={m.sportsNocSportSelectedIds ?? []}
                  fr={sportsNocUiFr(m)}
                  resolved={m.sportsNocSportOptionsResolved}
                  hist={hist}
                  isSending={isSending}
                  onToggle={onSportsNocSportToggle}
                  onConfirm={onSportsNocSportsConfirm}
                />
              ) : null}
              {m.uiType === 'folder_options' && m.options && m.options.length > 0 ? (
                <div
                  className={
                    m.folderOptionsResolved || hist
                      ? 'vnw-patientOptions vnw-patientOptions--resolved'
                      : 'vnw-patientOptions'
                  }
                  role="group"
                  aria-label={
                    m.folderOptionsResolved || hist
                      ? uiLangFr()
                        ? 'Choix de document (résolu)'
                        : 'Document choices (resolved)'
                      : uiLangFr()
                        ? 'Choix de document clinique'
                        : 'Clinical document choices'
                  }
                >
                  {m.folderOptionsResolved || hist ? (
                    <div className="vnw-patientOptionsResolvedNote" aria-hidden>
                      {uiLangFr() ? 'Sélection appliquée' : 'Selection applied'}
                    </div>
                  ) : null}
                  {m.options.map((opt) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-patientOptionBtn"
                      disabled={Boolean(isSending) || Boolean(m.folderOptionsResolved) || hist}
                      onClick={() => onFolderOptionSelect?.(opt.selection_id)}
                    >
                      <span className="vnw-patientOptionLabel">{opt.label}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {m.uiType === 'patient_options' && m.options && m.options.length > 0 ? (
                <div
                  className={
                    m.patientOptionsResolved || hist
                      ? 'vnw-patientOptions vnw-patientOptions--resolved'
                      : 'vnw-patientOptions'
                  }
                  role="group"
                  aria-label={
                    m.patientOptionsResolved || hist ? 'Patient choices (resolved)' : 'Patient choices'
                  }
                >
                  {m.patientOptionsResolved || hist ? (
                    <div className="vnw-patientOptionsResolvedNote" aria-hidden>
                      Selection applied — cards kept for context
                    </div>
                  ) : null}
                  {m.options.map((opt) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-patientOptionBtn"
                      disabled={Boolean(isSending) || Boolean(m.patientOptionsResolved) || hist}
                      onClick={() => onPatientOptionSelect?.(opt.selection_id)}
                    >
                      <span className="vnw-patientOptionLabel">{opt.label}</span>
                      <span className="vnw-patientOptionMeta">
                        <span className="vnw-patientOptionMetaPrefix">DOB</span>
                        <span className="vnw-patientOptionMetaValue">
                          {formatChatDateDisplay(opt.dob) || '—'}
                        </span>
                      </span>
                      <span className="vnw-patientOptionMeta">
                        <span className="vnw-patientOptionMetaPrefix">
                          {uiLangFr() ? 'Genre' : 'Gender'}
                        </span>
                        <span className="vnw-patientOptionMetaValue">{genderDisplay(opt.gender)}</span>
                      </span>
                    </button>
                  ))}
                  {!m.patientOptionsResolved &&
                  !hist &&
                  m.patientOptionsPaging?.hasMore &&
                  onPatientOptionsShowMore ? (
                    <button
                      type="button"
                      className="vnw-patientOptionsShowMore"
                      disabled={Boolean(isSending)}
                      onClick={() => onPatientOptionsShowMore()}
                      aria-label={`Show more matching patients, ${m.patientOptionsPaging.visibleCount} of ${m.patientOptionsPaging.total} currently shown`}
                    >
                      {uiLangFr()
                        ? 'Voir plus'
                        : 'Show more'}
                      <span className="vnw-patientOptionsShowMoreMeta" aria-hidden>
                        {' '}
                        ({m.patientOptionsPaging.visibleCount}/{m.patientOptionsPaging.total})
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}
              {m.uiType === 'assignee_options' &&
              m.options &&
              m.options.length > 0 &&
              !(
                secretaryPreviewInlineEdit?.field === 'recipient_name' &&
                secretaryPreviewInlineEdit?.messageId === m.id
              ) ? (
                <div
                  className={
                    m.assigneeOptionsResolved || hist
                      ? 'vnw-patientOptions vnw-patientOptions--resolved'
                      : 'vnw-patientOptions'
                  }
                  role="group"
                  aria-label={
                    m.assigneeOptionsResolved || hist
                      ? 'Recipient choices (resolved)'
                      : m.secretaryMessage
                        ? uiLangFr()
                          ? 'Choisir une secrétaire'
                          : 'Choose a secretary'
                        : 'Recipient choices'
                  }
                >
                  {m.assigneeOptionsResolved || hist ? (
                    <div className="vnw-patientOptionsResolvedNote" aria-hidden>
                      Recipient selected - cards kept for context
                    </div>
                  ) : null}
                  {m.options.map((opt, index) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-patientOptionBtn"
                      disabled={Boolean(isSending) || Boolean(m.assigneeOptionsResolved) || hist}
                      onClick={() => onAssigneeOptionSelect?.(opt.selection_id)}
                    >
                      <span className="vnw-patientOptionHeader">
                        <span className="vnw-patientOptionNumber" aria-hidden>
                          {index + 1}
                        </span>
                        <span className="vnw-patientOptionLabel">{opt.label}</span>
                      </span>
                      <span className="vnw-patientOptionMeta">
                        <span className="vnw-patientOptionMetaPrefix">DOB</span>
                        <span className="vnw-patientOptionMetaValue">
                          {formatChatDateDisplay(opt.dob) || '—'}
                        </span>
                      </span>
                      <span className="vnw-patientOptionMeta">
                        <span className="vnw-patientOptionMetaPrefix">
                          {uiLangFr() ? 'Genre' : 'Gender'}
                        </span>
                        <span className="vnw-patientOptionMetaValue">{genderDisplay(opt.gender)}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
              {m.uiType === 'recipient_type_options' && m.options && m.options.length > 0 ? (
                <div
                  className={
                    m.recipientTypeOptionsResolved || hist
                      ? 'vnw-sportsNocActionCards vnw-patientOptions--resolved'
                      : 'vnw-sportsNocActionCards'
                  }
                  role="group"
                  aria-label={
                    m.recipientTypeOptionsResolved || hist
                      ? 'Recipient type choices (resolved)'
                      : 'Recipient type choices'
                  }
                >
                  {m.options.map((opt) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-sportsNocActionBtn"
                      disabled={Boolean(isSending) || Boolean(m.recipientTypeOptionsResolved) || hist}
                      onClick={() => onRecipientTypeOptionSelect?.(opt.selection_id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {m.uiType === 'delivery_channel_options' && m.options && m.options.length > 0 ? (
                <div
                  className={
                    m.deliveryChannelOptionsResolved || hist
                      ? 'vnw-sportsNocActionCards vnw-patientOptions--resolved'
                      : 'vnw-sportsNocActionCards'
                  }
                  role="group"
                  aria-label={
                    m.deliveryChannelOptionsResolved || hist
                      ? uiLangFr()
                        ? 'Choix du canal (résolu)'
                        : 'Delivery channel choices (resolved)'
                      : uiLangFr()
                        ? 'Envoyer par Message ou Courriel'
                        : 'Send via Message or Mail'
                  }
                >
                  {m.options.map((opt) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-sportsNocActionBtn"
                      disabled={Boolean(isSending) || Boolean(m.deliveryChannelOptionsResolved) || hist}
                      onClick={() => onDeliveryChannelOptionSelect?.(opt.selection_id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {m.uiType === 'prescription_type_options' && m.options && m.options.length > 0 ? (
                <div
                  className="vnw-sportsNocActionCards vnw-sportsNocActionCards--singleColumn"
                  role="group"
                  aria-label={
                    m.bloodTestPrescriptionMeta
                      ? uiLangFr()
                        ? "Types d'ordonnance de prise de sang"
                        : 'Blood test prescription type choices'
                      : uiLangFr()
                        ? "Types d'ordonnance"
                        : 'Prescription type choices'
                  }
                >
                  {m.options.map((opt, index) => (
                    <button
                      key={opt.selection_id}
                      type="button"
                      className="vnw-sportsNocActionBtn"
                      disabled={
                        Boolean(isSending) || Boolean(m.prescriptionTypeOptionsResolved) || hist
                      }
                      onClick={() => onPrescriptionTypeOptionSelect?.(opt.selection_id)}
                    >
                      {index + 1}. {opt.label}
                    </button>
                  ))}
                </div>
              ) : null}
              </div>
              <time className="vnw-messageFooterTime" dateTime={new Date(m.timestamp).toISOString()}>
                {messageFooterTime(m)}
              </time>
              </div>
            )}
          </div>
        )
        return (
          <Fragment key={m.id}>
            {interMessageGap(messageIndex)}
            {messageBody}
          </Fragment>
        )
      })}
      {isSending || voiceIntentProcessing ? (
        <div className="vnw-message vnw-message--assistant" aria-label="Processing">
          {renderTypingDots()}
        </div>
      ) : null}
      <div ref={endRef} aria-hidden="true" />
    </div>
  )
}

